import { restoreWarState } from "../../utils/socialDepth";
import { activeProgramFromWire } from '../../utils/programEnrollment';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { V } from '../../utils/theme';
import { LS } from '../../utils/storage';
import { Icons } from '../../components/Icons';
import { track, onAccent } from '../../components/kit';
import { Card, Btn, Field, SkeletonCard, SuccessToastCtrl, ConfirmCtrl } from '../../components/ui';
import { today, fmtShort, fmtFull, uid, calc1RM, wUnit, chartCfg } from '../../utils/helpers';
import { CloudSync, notifyFeatureDisabled } from '../../utils/sync';
import { apiPost } from '../../utils/api';
import { cacheSignedUrl, usePhotoUrls } from '../../utils/photoSign';
import { localSrc } from '../../utils/photoUrls';
import { buildBackupPayload } from '../../utils/backup';
import { injuryIsActive, injuryJoint, normalizeBackupHealth } from '../../utils/healthWire';
import { convertStateUnits } from '../../utils/units';
import { Undo } from '../../utils/undo';
import { IRON_RANKS } from '../../data/ranks';
import { compressImage } from './utils';
// White-label (org) view of the platform. OrgContextCard renders `user_detail.org`
// and is shared with the User Audit panel's Gym tab; the helpers are the pure ones
// the unit tests cover (null-not-NaN formatting, the cron tile, severity colours).
import { OrgContextCard, severityColor } from '../admin/orgs/OrgContextCard';
import { cronStatus, num, severityLabel } from '../admin/orgs/helpers';

export function OneRMCalc({units}){
  const [w,setW]=useState("");
  const [r,setR]=useState("");
  const e1rm=w&&r?calc1RM(parseFloat(w),parseInt(r)):0;
  const pcts=[100,95,90,85,80,75,70,65,60];
  const u=wUnit(units);
  return(
    <div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
        <Field label={`Weight (${u})`} type="number" value={w} onChange={setW} placeholder="225"/>
        <Field label="Reps" type="number" value={r} onChange={setR} placeholder="5"/>
      </div>
      {e1rm>0&&(
        <div>
          <div style={{textAlign:"center",padding:16,background:`${V.accent}08`,borderRadius:14,marginBottom:14}}>
            <div style={{fontSize:11,color:V.text3,textTransform:"uppercase",letterSpacing:".08em"}}>Estimated 1RM</div>
            <div style={{fontSize:32,fontWeight:800,color:V.accent,fontFamily:V.mono}}>{e1rm}<span style={{fontSize:14}}> {u}</span></div>
          </div>
          <div style={{fontSize:11,color:V.text3,textTransform:"uppercase",letterSpacing:".08em",marginBottom:8,fontWeight:600}}>Percentage Chart</div>
          {pcts.map(p=>{
            const val=Math.round(e1rm*p/100);
            const reps=p===100?1:p>=95?2:p>=90?3:p>=85?5:p>=80?6:p>=75?8:p>=70?10:p>=65?12:15;
            return(
              <div key={p} style={{display:"flex",alignItems:"center",gap:10,padding:"8px 0",borderBottom:`1px solid ${V.divider}`}}>
                <span style={{width:36,fontSize:12,color:V.accent,fontFamily:V.mono,fontWeight:700}}>{p}%</span>
                <div style={{flex:1,height:6,borderRadius:3,background:V.inputBg}}>
                  <div style={{width:`${p}%`,height:"100%",borderRadius:3,background:V.accent,opacity:0.5}}/>
                </div>
                <span style={{fontSize:13,color:V.text,fontFamily:V.mono,fontWeight:600,minWidth:50,textAlign:"right"}}>{val} {u}</span>
                <span style={{fontSize:11,color:V.text3,minWidth:30}}>~{reps}r</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Progress Photos ───
// ─── Image Compression Utility ───
export function ProgressPhotos({s,d}){
  const fileRef=useRef(null);
  const [compressing,setCompressing]=useState(false);
  const [vaultLocked,setVaultLocked]=useState(true);
  const [pinInput,setPinInput]=useState("");
  const [showSetPin,setShowSetPin]=useState(false);
  const [newPin,setNewPin]=useState("");
  const [tab,setTab]=useState("public"); // public | vault

  // Vault PIN is stored encrypted at rest, so the read is async.
  // undefined = still loading, null = no vault configured.
  const [vaultPin,setVaultPin]=useState(undefined);
  useEffect(()=>{
    let on=true;
    LS.getSecure("ft-vault-pin").then(p=>{if(on)setVaultPin(p||null);});
    return()=>{on=false;};
  },[]);
  const vaultPinLoading=vaultPin===undefined;
  const hasVault=!!vaultPin;

  const unlockVault=()=>{
    if(pinInput===vaultPin){setVaultLocked(false);setPinInput("");SuccessToastCtrl.show("Vault unlocked");}
    else{SuccessToastCtrl.error("Wrong PIN");setPinInput("");}
  };
  const setupPin=async()=>{
    if(newPin.length>=4){
      const ok=await LS.setSecure("ft-vault-pin",newPin);
      if(!ok){SuccessToastCtrl.error("Couldn't secure the PIN on this device");return;}
      setVaultPin(newPin);setNewPin("");setShowSetPin(false);
      SuccessToastCtrl.show("Vault PIN set");
      // Sync PIN to server immediately
      CloudSync.debouncedPush({...s});
    }
  };

  const handlePhoto=async(e,isPrivate)=>{
    const file=e.target.files?.[0];
    if(!file)return;
    setCompressing(true);
    const isVideo=file.type.startsWith("video/");

    if(isVideo){
      // Upload video to R2 via API (supports mp4/mov up to 50MB)
      if(file.size>50*1024*1024){SuccessToastCtrl.error("Video too large (max 50MB)");setCompressing(false);e.target.value="";return;}
      const reader=new FileReader();
      reader.onload=async(ev)=>{
        const base64=ev.target.result;
        const blobUrl=URL.createObjectURL(file);
        const entry={id:uid(),date:today(),data:blobUrl,note:"",
          size:file.size,original:file.name,private:!!isPrivate,type:"video",mime:file.type};
        d({type:"ADD_PHOTO",photo:entry});
        if(s.profile?.email){
          try{
            const json=await apiPost("/api/photos/upload",{email:s.profile.email,photoId:entry.id,date:entry.date,
              data:base64,private:!!isPrivate,type:"video"},{email:s.profile.email,timeoutMs:180000});
            if(json?.url){
              // The signed URL is kept in memory for this session only; the entry keeps its id
              // (and the storage path) as the durable handle — see utils/photoUrls.js.
              cacheSignedUrl(entry.id,json.url,json.expires_in);
              d({type:"UPDATE_PHOTO",id:entry.id,url:json.url,path:json.path||null});
              SuccessToastCtrl.show(isPrivate?"Video saved to vault":"Video saved & synced ✓");
            }
            else{d({type:"DEL_PHOTO",id:entry.id});SuccessToastCtrl.error("Video upload failed — please try again");}
          }catch(e2){
            console.warn("Video upload failed:",e2);
            // A blob: URL does not survive a reload, so keeping the entry left a permanent dead tile
            d({type:"DEL_PHOTO",id:entry.id});
            // S5: the gym turned photos off (403 feature_disabled) — say so once instead of blaming the connection
            if(e2?.body?.error==="feature_disabled")notifyFeatureDisabled(e2.body.flag||"photos");
            else SuccessToastCtrl.error("Video upload failed — check your connection and try again");
          }
        }else{
          d({type:"DEL_PHOTO",id:entry.id});
          SuccessToastCtrl.show("Sign in to save videos — they're stored in the cloud");
        }
        setCompressing(false);
      };
      reader.onerror=()=>{setCompressing(false);SuccessToastCtrl.error("Couldn't read that video");};
      reader.readAsDataURL(file);
    }else{
      const compressed=await compressImage(file,800,0.7);
      if(compressed){
        const photo={id:uid(),date:today(),data:compressed,note:"",
          size:compressed.length,original:file.name,private:!!isPrivate,type:"photo"};
        d({type:"ADD_PHOTO",photo});
        if(s.profile?.email){
          try{
            const json=await apiPost("/api/photos/upload",{email:s.profile.email,photoId:photo.id,date:photo.date,
              data:compressed,private:!!isPrivate},{email:s.profile.email});
            // Image tiles render from the local base64 above; cache the signed URL anyway so a
            // device that later drops the base64 (500KB sync cap) still has something to show.
            if(json?.url)cacheSignedUrl(photo.id,json.url,json.expires_in);
          }catch(e2){
            console.warn("Photo upload failed:",e2);
            // The photo is still saved locally; the gym has just turned cloud photos off
            if(e2?.body?.error==="feature_disabled")notifyFeatureDisabled(e2.body.flag||"photos");
          }
        }
        SuccessToastCtrl.show(isPrivate?"Photo saved to vault":"Photo saved");
      }
      setCompressing(false);
    }
    e.target.value="";
  };

  const publicPhotos=(s.photos||[]).filter(p=>!p.private);
  const vaultPhotos=(s.photos||[]).filter(p=>p.private);
  // Photos synced to the cloud have no durable URL — resolve a short-lived signed one per
  // render. Local base64 (the usual case for images) needs no network and wins.
  const srcOf=usePhotoUrls(s.photos||[],s.profile?.email);

  const [viewPhoto,setViewPhoto]=useState(null); // full-screen photo viewer

  const PhotoViewer=()=>{
    if(!viewPhoto)return null;
    const allPhotos=tab==="vault"?vaultPhotos:publicPhotos;
    const p=viewPhoto;
    const idx=allPhotos.findIndex(x=>x.id===p.id);
    const hasPrev=idx>0;
    const hasNext=idx<allPhotos.length-1;
    return(
      <div style={{position:"fixed",top:0,left:0,right:0,bottom:0,zIndex:2000,background:"rgba(0,0,0,0.95)",display:"flex",flexDirection:"column",
        touchAction:"none"}} onClick={()=>setViewPhoto(null)}>
        {/* Top bar */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"12px 16px",flexShrink:0}}
          onClick={e=>e.stopPropagation()}>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:"#fff"}}>{fmtFull(p.date)}</div>
            {p.private&&<div style={{fontSize:11,color:V.purple}}>🔒 Private</div>}
          </div>
          <button onClick={()=>setViewPhoto(null)} style={{background:track(),border:"none",
            width:36,height:36,borderRadius:10,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center"}}>
            {Icons.x({size:18,color:"#fff"})}
          </button>
        </div>
        {/* Photo */}
        <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",padding:8,minHeight:0,position:"relative"}}>
          {hasPrev&&(
            <button onClick={e=>{e.stopPropagation();setViewPhoto(allPhotos[idx-1]);}}
              style={{position:"absolute",left:8,top:"50%",transform:"translateY(-50%)",zIndex:10,
                width:40,height:40,borderRadius:20,background:track(),border:"none",cursor:"pointer",
                display:"flex",alignItems:"center",justifyContent:"center"}}>
              {Icons.chevLeft({size:20,color:"#fff"})}
            </button>
          )}
          {!srcOf(p).src?(
            <div style={{color:"#888",textAlign:"center",padding:"40px",fontSize:13}} onClick={e=>e.stopPropagation()}>
              {srcOf(p).state==="pending"?"Loading…":"Unavailable offline"}
            </div>
          ):p.type==="video"?(
            <video src={srcOf(p).src} controls autoPlay playsInline
              onError={e=>{
                const d=document.createElement("div");
                d.textContent="Video unavailable";
                Object.assign(d.style,{color:"#888",textAlign:"center",padding:"40px"});
                e.target.replaceWith(d);
              }}
              style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain",borderRadius:8}}
              onClick={e=>e.stopPropagation()}/>
          ):(
            <img src={srcOf(p).src} alt={`Progress photo from ${fmtShort(p.date)}`} style={{maxWidth:"100%",maxHeight:"100%",objectFit:"contain",borderRadius:8}}
              onClick={e=>e.stopPropagation()}/>
          )}
          {hasNext&&(
            <button onClick={e=>{e.stopPropagation();setViewPhoto(allPhotos[idx+1]);}}
              style={{position:"absolute",right:8,top:"50%",transform:"translateY(-50%)",zIndex:10,
                width:40,height:40,borderRadius:20,background:track(),border:"none",cursor:"pointer",
                display:"flex",alignItems:"center",justifyContent:"center"}}>
              {Icons.chevRight({size:20,color:"#fff"})}
            </button>
          )}
        </div>
        {/* Bottom bar */}
        <div style={{padding:"8px 16px 20px",display:"flex",justifyContent:"center",gap:12,flexShrink:0}}
          onClick={e=>e.stopPropagation()}>
          <span style={{fontSize:11,color:"rgba(255,255,255,0.5)"}}>{idx+1} of {allPhotos.length}</span>
          <button onClick={()=>{ConfirmCtrl.show("Delete this photo?","This cannot be undone.",()=>{Undo.set("Photo deleted",p,"photo");d({type:"DEL_PHOTO",id:p.id});setViewPhoto(null);SuccessToastCtrl.show("Photo deleted");});}}
            style={{background:"none",border:"none",cursor:"pointer",fontSize:11,color:V.danger,fontFamily:V.font}}>
            Delete
          </button>
        </div>
      </div>
    );
  };

  const PhotoGrid=({photos,isVault})=>(
    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginTop:14}}>
      {photos.map(p=>(
        <div key={p.id} onClick={()=>setViewPhoto(p)} style={{position:"relative",borderRadius:14,overflow:"hidden",aspectRatio:"3/4",
          border:`1px solid ${isVault?"rgba(168,85,247,0.3)":V.cardBorder}`,cursor:"pointer"}}>
          {!srcOf(p).src?(
            <div style={{width:"100%",height:"100%",display:"flex",alignItems:"center",justifyContent:"center",
              background:V.inputBg,fontSize:11,color:V.text3,textAlign:"center",paddingLeft:8,paddingRight:8}}>
              {srcOf(p).state==="pending"?"Loading…":"Unavailable offline"}
            </div>
          ):p.type==="video"?(
            <video src={srcOf(p).src} muted playsInline preload="metadata" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
          ):(
            <img loading="lazy" src={srcOf(p).src} alt={`Progress photo from ${fmtShort(p.date)}`} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
          )}
          {p.type==="video"&&(
            <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",
              width:36,height:36,borderRadius:18,background:"rgba(0,0,0,0.6)",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <span style={{fontSize:16,color:"#fff",marginLeft:2}}>▶</span>
            </div>
          )}
          <div style={{position:"absolute",bottom:0,left:0,right:0,padding:"20px 10px 8px",
            background:"linear-gradient(transparent,rgba(0,0,0,0.8))"}}>
            <div style={{fontSize:11,color:"#fff",fontWeight:600}}>{fmtShort(p.date)}</div>
            <div style={{display:"flex",gap:4,alignItems:"center"}}>
              {p.type==="video"&&<span style={{fontSize:11,color:V.accent2}}>🎥 Video</span>}
              {isVault&&<span style={{fontSize:11,color:V.purple}}>🔒 Private</span>}
            </div>
          </div>
          <button onClick={e=>{e.stopPropagation();ConfirmCtrl.show(`Delete this ${p.type==="video"?"video":"photo"}?`,"This cannot be undone.",()=>{Undo.set("Deleted",p,"photo");d({type:"DEL_PHOTO",id:p.id});SuccessToastCtrl.show("Deleted");});}} style={{position:"absolute",top:6,right:6,
            width:28,height:28,borderRadius:8,background:"rgba(0,0,0,0.6)",border:"none",cursor:"pointer",
            display:"flex",alignItems:"center",justifyContent:"center"}}>{Icons.x({size:12,color:"#fff"})}</button>
        </div>
      ))}
    </div>
  );

  return(
    <div>
      <PhotoViewer/>
      {/* Tab toggle */}
      <div style={{display:"flex",gap:6,marginBottom:12}}>
        <button onClick={()=>setTab("public")} style={{flex:1,padding:"8px",borderRadius:8,
          border:`1.5px solid ${tab==="public"?V.accent:V.cardBorder}`,
          background:tab==="public"?`${V.accent}10`:"transparent",
          cursor:"pointer",fontSize:11,fontWeight:700,color:tab==="public"?V.accent:V.text3,fontFamily:V.font}}>
          📸 Photos
        </button>
        <button onClick={()=>setTab("vault")} style={{flex:1,padding:"8px",borderRadius:8,
          border:`1.5px solid ${tab==="vault"?"#a855f7":V.cardBorder}`,
          background:tab==="vault"?"rgba(168,85,247,0.08)":"transparent",
          cursor:"pointer",fontSize:11,fontWeight:700,color:tab==="vault"?"#a855f7":V.text3,fontFamily:V.font}}>
          🔒 Private Vault {vaultPhotos.length>0?`(${vaultPhotos.length})`:""}
        </button>
      </div>

      {tab==="public"?(
        <div>
          <input ref={fileRef} type="file" accept={"image/" + "*,video/" + "*"} onChange={e=>handlePhoto(e,false)}
            style={{display:"none"}}/>
          <Btn full onClick={()=>fileRef.current?.click()} disabled={compressing}>
            {compressing?<span>Compressing…</span>:<span>{Icons.plus({size:16,color:V.bg})} Add Photo or Video</span>}
          </Btn>
          {publicPhotos.length>0&&(
            <div style={{fontSize:11,color:V.text3,marginTop:4,textAlign:"center"}}>
              {publicPhotos.length} photo{publicPhotos.length>1?"s":""} · Compressed for cloud sync
            </div>
          )}
          <PhotoGrid photos={publicPhotos} isVault={false}/>
          {publicPhotos.length===0&&(
            <div style={{textAlign:"center",padding:30,color:V.text3,fontSize:13}}>No photos yet. Start tracking your progress!</div>
          )}
        </div>
      ):(
        <div>
          {vaultPinLoading?(
            <Card style={{padding:20,textAlign:"center"}}>
              <div style={{width:22,height:22,borderRadius:11,border:`2px solid ${V.accent}30`,
                borderTopColor:V.accent,animation:"spin .8s linear infinite",margin:"0 auto"}}/>
            </Card>
          ):!hasVault?(
            <Card style={{padding:20,textAlign:"center"}}>
              <div style={{fontSize:28,marginBottom:8}}>🔒</div>
              <div style={{fontSize:14,fontWeight:700,color:V.text,marginBottom:4}}>Set up Private Vault</div>
              <div style={{fontSize:11,color:V.text3,marginBottom:14,lineHeight:1.5}}>
                Protect sensitive progress photos with a 4+ digit PIN. These photos won't be visible without the code.
              </div>
              {showSetPin?(
                <div style={{display:"flex",gap:8}}>
                  <input type="password" inputMode="numeric" value={newPin} onChange={e=>setNewPin(e.target.value.replace(/\D/g,""))}
                    placeholder="Enter PIN (4+ digits)" maxLength={8}
                    style={{flex:1,padding:"12px 14px",background:V.inputBg,border:`1px solid ${V.cardBorder}`,
                      borderRadius:10,color:V.text,fontSize:16,textAlign:"center",fontFamily:V.mono,letterSpacing:8,outline:"none"}}/>
                  <Btn onClick={setupPin} disabled={newPin.length<4}>Set</Btn>
                </div>
              ):(
                <Btn full onClick={()=>setShowSetPin(true)} s={{background:"#a855f7"}}>Create Vault PIN</Btn>
              )}
            </Card>
          ):vaultLocked?(
            <Card style={{padding:20,textAlign:"center"}}>
              <div style={{fontSize:28,marginBottom:8}}>🔒</div>
              <div style={{fontSize:14,fontWeight:700,color:V.text,marginBottom:4}}>Vault Locked</div>
              <div style={{fontSize:11,color:V.text3,marginBottom:14}}>Enter your PIN to view private photos</div>
              <div style={{display:"flex",gap:8,maxWidth:240,margin:"0 auto"}}>
                <input type="password" inputMode="numeric" value={pinInput} onChange={e=>setPinInput(e.target.value.replace(/\D/g,""))}
                  placeholder="PIN" maxLength={8} onKeyDown={e=>{if(e.key==="Enter")unlockVault();}}
                  style={{flex:1,padding:"12px 14px",background:V.inputBg,border:`1px solid ${V.cardBorder}`,
                    borderRadius:10,color:V.text,fontSize:18,textAlign:"center",fontFamily:V.mono,letterSpacing:8,outline:"none"}}/>
                <Btn onClick={unlockVault} s={{background:"#a855f7"}}>Unlock</Btn>
              </div>
            </Card>
          ):(
            <div>
              <div style={{display:"flex",gap:8,marginBottom:8}}>
                <input ref={fileRef} type="file" accept={"image/" + "*,video/" + "*"} onChange={e=>handlePhoto(e,true)}
                  style={{display:"none"}}/>
                <Btn full onClick={()=>fileRef.current?.click()} disabled={compressing} s={{background:"#a855f7"}}>
                  {compressing?<span>Compressing…</span>:<span>{Icons.plus({size:16,color:V.bg})} Add to Vault 📸🎥</span>}
                </Btn>
                <Btn v="secondary" onClick={()=>{setVaultLocked(true);SuccessToastCtrl.show("Vault locked");}} s={{flexShrink:0}}>
                  🔒 Lock
                </Btn>
              </div>
              <PhotoGrid photos={vaultPhotos} isVault={true}/>
              {vaultPhotos.length===0&&(
                <div style={{textAlign:"center",padding:30,color:V.text3,fontSize:13}}>Vault is empty. Add private progress photos here.</div>
              )}
              <button onClick={()=>{ConfirmCtrl.show("Reset vault PIN?","This won't delete photos.",()=>{LS.del("ft-vault-pin");setVaultPin(null);setVaultLocked(true);SuccessToastCtrl.show("Vault PIN reset");});}}
                style={{width:"100%",marginTop:12,padding:8,background:"none",border:"none",cursor:"pointer",
                  fontSize:11,color:V.text3,fontFamily:V.font}}>Reset Vault PIN</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Export & Import ───
// ─── Profile Editor (Settings) ───
export function ProfileEditor({s,d}){
  const p=s.profile||{};
  const [form,setForm]=useState({...p});
  // Re-seed after a cloud restore / demo load so Save doesn't overwrite fresh data with stale fields.
  // Keyed on the identity fields on purpose: depending on the whole `s.profile` object would
  // clobber whatever the user is typing on every unrelated profile write.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(()=>{setForm({...(s.profile||{})});},[s.profile?.email,s.profile?.firstName,s.profile?.lastName,s.profile?.dob,s.profile?.height]);
  const [saved,setSaved]=useState(false);
  const [syncing,setSyncing]=useState(false);

  const saveProfile=async()=>{
    d({type:"SET_PROFILE",profile:{...s.profile,...form}});
    setSyncing(true);
    // Authenticated update (was sent without the session token and silently ignored)
    let ok=true;
    try{await apiPost("/api/users",{action:"update",...form,updatedAt:new Date().toISOString()});}
    catch(e){ok=false;console.warn("Profile update failed:",e);}
    try{await window.__ironlog_sync?.();}catch(e){}
    setSyncing(false);
    setSaved(true);
    SuccessToastCtrl.show(ok?"Profile updated":"Saved on this device — cloud update failed, will retry on next sync",{tone:ok?"success":"error"});
    setTimeout(()=>setSaved(false),2000);
  };

  const upd=(k,v)=>setForm(f=>({...f,[k]:v}));

  return(
    <div style={{display:"flex",flexDirection:"column",gap:8}}>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
        <Field label="First Name" value={form.firstName||""} onChange={v=>upd("firstName",v)}/>
        <Field label="Last Name" value={form.lastName||""} onChange={v=>upd("lastName",v)}/>
      </div>
      <Field label="Nickname" value={form.nickname||""} onChange={v=>upd("nickname",v)} placeholder="Display name"/>
      <div style={{marginBottom:10}}>
        <div style={{fontSize:11,color:V.text3,marginBottom:4,fontWeight:600}}>Email</div>
        <div style={{padding:"10px 14px",background:V.card,border:`1px solid ${V.cardBorder}`,
          borderRadius:10,fontSize:14,color:V.text3,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span>{form.email||"—"}</span>
          <span style={{fontSize:11,color:V.text3,background:V.inputBg,padding:"2px 6px",borderRadius:4}}>Locked</span>
        </div>
        <div style={{fontSize:11,color:V.text3,marginTop:3}}>Email cannot be changed. It's your account identifier.</div>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
        <Field label="Date of Birth" type="date" value={form.dob||""} onChange={v=>upd("dob",v)}/>
        <div>
          <div style={{fontSize:11,color:V.text3,textTransform:"uppercase",letterSpacing:".08em",marginBottom:6,fontWeight:600}}>Sex</div>
          <div style={{display:"flex",gap:6}}>
            {["Male","Female"].map(sx=>(
              <button key={sx} onClick={()=>upd("sex",sx)} style={{
                flex:1,padding:"8px 0",borderRadius:8,border:`1.5px solid ${form.sex===sx?V.accent:V.cardBorder}`,
                background:form.sex===sx?`${V.accent}10`:V.card,
                cursor:"pointer",WebkitTapHighlightColor:"transparent",
                fontSize:12,fontWeight:600,color:form.sex===sx?V.accent:V.text,fontFamily:V.font,textAlign:"center"
              }}>{sx}</button>
            ))}
          </div>
        </div>
      </div>
      <Field label="Height" value={form.height||""} onChange={v=>upd("height",v)} placeholder="5'10"/>
      <Btn full onClick={saveProfile} s={{marginTop:4}}>
        {syncing?"Saving...":<>{Icons.check({size:14,color:V.bg})} {saved?"Saved!":"Save Profile"}</>}
      </Btn>
    </div>
  );
}

// ─── Training profile (Settings) — every preference the setup wizard collects ───
const TP_EQUIPMENT=[{id:"gym",l:"Full gym"},{id:"home",l:"Home setup"},{id:"bodyweight",l:"Bodyweight"},{id:"machines",l:"Machines only"}];
const TP_SESSIONS=[30,45,60,90];
const TP_TIMES=["Morning","Midday","Evening","Flexible"];
const TP_DIETS=["No preference","Vegetarian","Vegan","Pescatarian","Halal","Kosher"];
const TP_ALLERGIES=["Dairy","Gluten","Nuts","Eggs","Shellfish","Soy"];
export function TrainingProfileEditor({s,d}){
  const p=s.profile||{};
  const u=wUnit(s.units);
  const [form,setForm]=useState(()=>({
    equipment:p.equipment||"gym",sessionLength:parseInt(p.sessionLength)||60,trainingTime:p.trainingTime||"Flexible",
    weeklyAvailability:parseInt(p.weeklyAvailability)||4,diet:p.diet||"No preference",allergies:Array.isArray(p.allergies)?p.allergies:[],
    bench:p.lifts?.bench||"",squat:p.lifts?.squat||"",deadlift:p.lifts?.deadlift||"",
  }));
  const [saved,setSaved]=useState(false);
  const upd=(k,v)=>setForm(f=>({...f,[k]:v}));
  const activeInjuries=(s.injuries||[]).filter(injuryIsActive);
  const save=async()=>{
    const hasLifts=parseFloat(form.bench)||parseFloat(form.squat)||parseFloat(form.deadlift);
    const next={...p,equipment:form.equipment,sessionLength:form.sessionLength,trainingTime:form.trainingTime==="Flexible"?null:form.trainingTime,
      weeklyAvailability:form.weeklyAvailability,diet:form.diet==="No preference"?null:form.diet,allergies:form.allergies,
      lifts:hasLifts?{bench:parseFloat(form.bench)||0,squat:parseFloat(form.squat)||0,deadlift:parseFloat(form.deadlift)||0,units:s.units}:null};
    d({type:"SET_PROFILE",profile:next});
    try{await window.__ironlog_sync?.();}catch(e){}
    setSaved(true);setTimeout(()=>setSaved(false),2000);
    SuccessToastCtrl.show("Training profile saved — coach, programs and meal plan updated");
  };
  const Seg=({label,options,value,onChange,render})=>(
    <div>
      <div style={{fontSize:11,color:V.text3,textTransform:"uppercase",letterSpacing:".08em",marginBottom:6,fontWeight:600}}>{label}</div>
      <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
        {options.map(o=>{const id=typeof o==="object"?o.id:o;const l=typeof o==="object"?o.l:(render?render(o):String(o));const on=value===id;return(
          <button key={id} type="button" onClick={()=>onChange(id)} style={{padding:"7px 12px",borderRadius:8,border:`1.5px solid ${on?V.accent:V.cardBorder}`,
            background:on?`${V.accent}10`:V.card,cursor:"pointer",fontSize:12,fontWeight:600,color:on?V.accent:V.text,fontFamily:V.font}}>{l}</button>);})}
      </div>
    </div>
  );
  return(
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div style={{fontSize:11,color:V.text3,lineHeight:1.5}}>These drive the coach, program warnings, meal plan and supplement suggestions. Change them any time without redoing setup.</div>
      <Seg label="Equipment" options={TP_EQUIPMENT} value={form.equipment} onChange={v=>upd("equipment",v)}/>
      <Seg label="Session length" options={TP_SESSIONS} value={form.sessionLength} onChange={v=>upd("sessionLength",v)} render={o=>`${o} min`}/>
      <Seg label="Days per week" options={[2,3,4,5,6,7]} value={form.weeklyAvailability} onChange={v=>upd("weeklyAvailability",v)}/>
      <Seg label="Usual training time" options={TP_TIMES} value={form.trainingTime} onChange={v=>upd("trainingTime",v)}/>
      <Seg label="Diet" options={TP_DIETS} value={form.diet} onChange={v=>upd("diet",v)}/>
      <div>
        <div style={{fontSize:11,color:V.text3,textTransform:"uppercase",letterSpacing:".08em",marginBottom:6,fontWeight:600}}>Allergies</div>
        <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
          {TP_ALLERGIES.map(a=>{const on=form.allergies.includes(a);return(
            <button key={a} type="button" onClick={()=>upd("allergies",on?form.allergies.filter(x=>x!==a):[...form.allergies,a])} style={{padding:"7px 12px",borderRadius:8,border:`1.5px solid ${on?V.warn:V.cardBorder}`,
              background:on?`${V.warn}12`:V.card,cursor:"pointer",fontSize:12,fontWeight:600,color:on?V.warn:V.text,fontFamily:V.font}}>{a}</button>);})}
        </div>
      </div>
      <div>
        <div style={{fontSize:11,color:V.text3,textTransform:"uppercase",letterSpacing:".08em",marginBottom:6,fontWeight:600}}>Current lifts ({u}) · baseline until logged sets beat them</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
          <Field label="Bench" type="number" value={form.bench} onChange={v=>upd("bench",v)} unit={u}/>
          <Field label="Squat" type="number" value={form.squat} onChange={v=>upd("squat",v)} unit={u}/>
          <Field label="Deadlift" type="number" value={form.deadlift} onChange={v=>upd("deadlift",v)} unit={u}/>
        </div>
      </div>
      <button type="button" onClick={()=>d({type:"TAB",tab:"plan_injuries"})} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10,padding:"10px 12px",borderRadius:10,
        background:V.card,border:`1px solid ${V.cardBorder}`,cursor:"pointer",textAlign:"left",fontFamily:V.font}}>
        <div>
          <div style={{fontSize:12,fontWeight:700,color:V.text}}>Injuries</div>
          <div style={{fontSize:11,color:V.text3}}>{activeInjuries.length?`Active: ${activeInjuries.map(i=>String(injuryJoint(i)||"").replace(/_/g," ")).join(", ")}`:"None flagged — the coach uses these to swap exercises"}</div>
        </div>
        {Icons.chevRight({size:14,color:V.text3})}
      </button>
      <Btn full onClick={save}>{Icons.check({size:14,color:onAccent()})} {saved?"Saved!":"Save training profile"}</Btn>
    </div>
  );
}

// ─── Admin Panel (only accessible with is_admin=true on server) ───
// Caps for the derived org badge on the users list: at most this many orgs, each read in
// pages of this size, at most this many pages per org. Production has one org with two
// members; the caps exist so a large tenant can never turn one screen into hundreds of calls.
const ORG_MAP_MAX_ORGS=10,ORG_MAP_PAGE=200,ORG_MAP_MAX_PAGES=5;

// One user's gym, as a row badge. `known` is false while the map is still loading or when
// the roster could not be read — an unknown membership must never render as "Solo".
function OrgBadge({org,known}){
  if(org){
    const c=org.status==="active"?V.accent:org.status==="suspended"?V.warn:V.danger;
    return(
      <span data-testid="user-org-badge" data-org={org.slug} style={{display:"inline-flex",alignItems:"center",gap:4,marginTop:2,
        fontSize:9,fontWeight:700,color:c,background:`${c}14`,border:`1px solid ${c}30`,borderRadius:6,
        paddingTop:1,paddingBottom:1,paddingLeft:6,paddingRight:6,maxWidth:"100%",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
        🏢 {org.name}{org.role&&org.role!=="member"?` · ${org.role}`:""}{org.status!=="active"?` · ${org.status}`:""}
      </span>
    );
  }
  if(!known)return null;
  return <span data-testid="user-org-badge" data-org="" style={{display:"inline-block",marginTop:2,fontSize:9,color:V.text3}}>Solo</span>;
}

// ─── White-label dashboard block (dashboard.whitelabel) ───
// `available:false` ships the same keys with every counter at 0, so the zeros are not facts
// and are not rendered as such.
function WhiteLabelDash({wl,adminEmailConfigured}){
  const cron=cronStatus(wl.cron);
  const cronColor=severityColor(cron.severity);
  const tile=(l,v,c)=>(
    <div key={l} style={{textAlign:"center",paddingTop:6,paddingBottom:6,paddingLeft:4,paddingRight:4,borderRadius:6,background:V.card}}>
      <div style={{fontSize:16,fontWeight:800,color:c||V.text,fontFamily:V.mono}}>{v}</div>
      <div style={{fontSize:11,color:V.text3}}>{l}</div>
    </div>
  );
  return(
    <Card style={{paddingTop:14,paddingBottom:14,paddingLeft:14,paddingRight:14}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8,flexWrap:"wrap"}}>
        <div style={{fontSize:12,fontWeight:700,color:V.text,flex:1,minWidth:120}}>White-label{wl.billing_month?` · ${wl.billing_month}`:""}</div>
        {!wl.available&&<span data-testid="wl-unavailable" style={{fontSize:9,fontWeight:700,color:V.warn,background:`${V.warn}14`,borderRadius:6,paddingTop:2,paddingBottom:2,paddingLeft:8,paddingRight:8,textTransform:"uppercase",letterSpacing:".06em"}}>Unavailable</span>}
      </div>
      {!wl.available&&(
        <div data-testid="wl-unavailable-note" style={{fontSize:11,color:V.text3,lineHeight:1.5,marginBottom:8}}>
          The analytics function on the API did not answer, so there are no numbers to show — these are not zeros. Apply the admin analytics migration and reload.
        </div>
      )}
      {wl.available&&(
        <div data-testid="wl-dash-grid" style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6}}>
          {tile("ORGS",num(wl.orgs?.total),V.accent)}
          {tile("ACTIVE",num(wl.orgs?.active),"#22c55e")}
          {tile("SUSPENDED",num(wl.orgs?.suspended),V.warn)}
          {tile("CLOSED",num(wl.orgs?.closed),V.danger)}
          {tile("MEMBERS IN ORGS",num(wl.members_in_orgs),V.accent2)}
          {tile("BILLABLE (MO)",num(wl.active_clients_month),V.purple)}
          {tile("PROGRAMS",num(wl.programs_assigned),"#06b6d4")}
          {tile("MESSAGES 7D",num(wl.messages_7d),"#ec4899")}
          {tile("ANNOUNCE 30D",num(wl.announcements_30d),"#f97316")}
          {tile("QUEUE PENDING",num(wl.email_queue?.pending),(wl.email_queue?.pending||0)>0?V.warn:V.text3)}
          {tile("QUEUE FAILED",num(wl.email_queue?.failed),(wl.email_queue?.failed||0)>0?V.danger:V.text3)}
        </div>
      )}
      {/* The cron tile stays visible even when the analytics RPC is unavailable: a cron that
          has never run is the single most consequential thing on this screen. */}
      <div data-testid="wl-cron" style={{marginTop:10,borderRadius:10,borderLeft:`3px solid ${cronColor}`,background:V.card,
        paddingTop:8,paddingBottom:8,paddingLeft:10,paddingRight:10}}>
        <div style={{display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
          <div style={{fontSize:11,fontWeight:700,color:V.text,flex:1,minWidth:120}}>Cron /api/cron/org</div>
          <span data-testid="wl-cron-state" style={{fontSize:11,fontWeight:800,color:cronColor,fontFamily:V.mono}}>{cron.label}</span>
        </div>
        <div style={{fontSize:10,color:V.text3,lineHeight:1.5,marginTop:2}}>{cron.note}</div>
      </div>
      {adminEmailConfigured===false&&(
        <div data-testid="wl-admin-email-note" style={{fontSize:10,color:V.warn,lineHeight:1.5,marginTop:6}}>
          ADMIN_EMAIL is not set on the API, so no account is the super-admin and the Organizations screen is unavailable to everyone.
        </div>
      )}
    </Card>
  );
}

// ─── White-label health block (health.whitelabel) ───
// A flat severity list the API assembles; the cron row is rendered first because every
// other overdue-ish number below it is usually a consequence of the cron not running.
function WhiteLabelHealth({wl}){
  const cron=cronStatus(wl.cron);
  const rows=[{label:wl.cron?.label||"Cron /api/cron/org",count:null,severity:wl.cron?.severity||cron.severity,note:wl.cron?.note||cron.note,testId:"wl-health-cron"},
    ...(wl.items||[]).map(i=>({label:i.label,count:i.count,severity:i.severity,note:i.detail?JSON.stringify(i.detail):null}))];
  const worst=wl.worst||"ok";
  const worstColor=severityColor(worst);
  return(
    <Card style={{paddingTop:14,paddingBottom:14,paddingLeft:14,paddingRight:14}}>
      <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:8,flexWrap:"wrap"}}>
        <div style={{fontSize:12,fontWeight:700,color:V.text,flex:1,minWidth:120}}>White-label health{wl.month?` · ${wl.month.slice(0,7)}`:""}</div>
        <span data-testid="wl-health-worst" style={{fontSize:9,fontWeight:800,color:worstColor,background:`${worstColor}14`,border:`1px solid ${worstColor}30`,
          borderRadius:6,paddingTop:2,paddingBottom:2,paddingLeft:8,paddingRight:8,textTransform:"uppercase",letterSpacing:".06em"}}>{severityLabel(worst)}</span>
      </div>
      <div data-testid="wl-health-items">
        {rows.map((r,i)=>{
          const c=severityColor(r.severity);
          return(
            <div key={r.label} data-testid={r.testId||"wl-health-item"} data-severity={r.severity}
              style={{display:"flex",alignItems:"flex-start",gap:8,paddingTop:6,paddingBottom:6,borderTop:i?`1px solid ${V.divider}`:"none"}}>
              <span aria-hidden style={{width:8,height:8,borderRadius:4,background:c,flexShrink:0,marginTop:4}}/>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:12,color:V.text}}>{r.label}</div>
                {r.note&&<div style={{fontSize:10,color:V.text3,lineHeight:1.5,marginTop:1,wordBreak:"break-word"}}>{r.note}</div>}
              </div>
              {r.count!==null&&<span style={{fontSize:13,fontWeight:800,color:c,fontFamily:V.mono,flexShrink:0}}>{num(r.count)}</span>}
            </div>
          );
        })}
      </div>
      {wl.truncated&&<div style={{fontSize:10,color:V.warn,marginTop:6,lineHeight:1.5}}>The checks hit their row cap, so the orphan counts are a lower bound.</div>}
    </Card>
  );
}

// `isSuperAdmin` / `adminEmailConfigured` come from App.jsx's `/api/admin {action:'check'}`.
// `undefined` means "not yet known" (an older build, or the check has not answered) and every
// screen behaves exactly as it did before.
export function AdminPanel({s,initialView,isSuperAdmin,adminEmailConfigured}){
  const [view,setView]=useState(initialView||"dashboard");
  const [dash,setDash]=useState(null);
  const [users,setUsers]=useState([]);
  // `users` now also answers with exact platform totals and whether its per-user tallies
  // hit the API's row cap; the headline numbers read those instead of summing the page.
  const [userTotals,setUserTotals]=useState(null);
  const [usersTruncated,setUsersTruncated]=useState(false);
  const [orgMap,setOrgMap]=useState(null); // {state, by:{email→{name,slug,status,role}}, note}
  const [selUser,setSelUser]=useState(null);
  const [userData,setUserData]=useState(null);
  const [health,setHealth]=useState(null);
  const [groups,setGroups]=useState([]);
  const [dmData,setDmData]=useState(null);
  const [gcData,setGcData]=useState(null);
  const [search,setSearch]=useState("");
  const [sort,setSort]=useState({key:"created",dir:"desc"});
  const [filter,setFilter]=useState("all");
  const [loading,setLoading]=useState(false);
  const [dmUserA,setDmUserA]=useState("");
  const [dmUserB,setDmUserB]=useState("");
  const [selGroupCode,setSelGroupCode]=useState("");
  const [enlargedPhoto,setEnlargedPhoto]=useState(null);
  // Prefer a signed URL for every photo row that has an id. Signing is scoped to the signed-in
  // account, so another user's rows come back unsigned and fall back to their legacy public_url
  // until the API can sign on an admin's behalf.
  const adminSrcOf=usePhotoUrls(userData?.photos||[],s.profile?.email);
  const [banResult,setBanResult]=useState(null);
  const [banWorking,setBanWorking]=useState(false);
  const af=async(action,extra={})=>{
    try{return await apiPost("/api/admin",{action,...extra});}
    catch(e){return null;}
  };
  // Mount only: `initialView` is the entry route and the loaders are stable closures over af().
  useEffect(()=>{
    af("dashboard").then(setDash);
    if(initialView==="users"||initialView==="lookup"||initialView==="moderation")loadUsers();
    if(initialView==="health")loadHealth();
    if(initialView==="moderation")loadGroups();
  },[]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUsers=()=>{
    setLoading(true);
    af("users").then(r=>{
      setUsers(r?.users||[]);
      setUserTotals(r?.totals||null);
      setUsersTruncated(!!r?.truncated);
      setLoading(false);
    });
    loadOrgMap();
  };

  // ─── Org badge for the users list ───
  // The `users` action is the platform list and carries no org membership, and adding it
  // to the API was out of scope here, so the badge is derived: one admin-level `org_list`,
  // then each org's roster from /api/org `list_members` (super-admin only). The first
  // refusal stops the loop and the column says why, rather than showing a wrong "solo".
  const loadOrgMap=async()=>{
    if(orgMap?.state==="loading")return;
    setOrgMap({state:"loading",by:{},note:""});
    let list=null;
    try{list=await apiPost("/api/admin",{action:"org_list"});}catch(e){list=null;}
    const orgs=list?.orgs||[];
    if(!list){setOrgMap({state:"error",by:{},note:"Could not read the organization list."});return;}
    if(!orgs.length){setOrgMap({state:"ready",by:{},note:"No organizations yet — every account is solo."});return;}
    if(isSuperAdmin===false){
      setOrgMap({state:"denied",by:{},note:`${orgs.length} organization${orgs.length===1?"":"s"} exist, but only the super-admin can read their rosters — open a user to see their gym.`});
      return;
    }
    const by={};
    const note=orgs.length>ORG_MAP_MAX_ORGS?`Membership shown for the first ${ORG_MAP_MAX_ORGS} of ${orgs.length} organizations.`:"";
    for(const o of orgs.slice(0,ORG_MAP_MAX_ORGS)){
      let cursor=null;
      for(let i=0;i<ORG_MAP_MAX_PAGES;i++){
        let r=null;
        try{r=await apiPost("/api/org",{action:"list_members",org_id:o.id,limit:ORG_MAP_PAGE,...(cursor?{cursor}:{})});}catch(e){r=null;}
        if(!r){setOrgMap({state:"denied",by,note:"Gym membership needs the super-admin account — open a user to see their gym."});return;}
        for(const m of r.members||[])by[String(m.email||"").toLowerCase()]={name:o.name,slug:o.slug,status:o.status,role:m.role};
        cursor=r.next_cursor;
        if(!cursor)break;
      }
    }
    setOrgMap({state:"ready",by,note});
  };
  const orgOf=(email)=>orgMap?.by?.[String(email||"").toLowerCase()]||null;
  const loadDetail=(email)=>{setLoading(true);setSelUser(email);setBanResult(null);af("user_detail",{email}).then(r=>{setUserData(r);setLoading(false);setView("detail");});};
  const banUser=async(email,ban)=>{
    setBanWorking(true);setBanResult(null);
    const r=await af("ban_user",{email,ban});
    if(r?.success){
      setBanResult({ok:true,msg:ban?"User banned — all sessions revoked":"User unbanned successfully"});
      setUserData(d=>({...d,profile:{...d.profile,is_banned:ban}}));
    }else setBanResult({ok:false,msg:"Failed — check admin session"});
    setBanWorking(false);
  };
  const loadHealth=()=>{af("health").then(setHealth);};
  const loadGroups=()=>{af("groups").then(r=>setGroups(r?.groups||[]));};
  const loadDMs=()=>{if(!dmUserA||!dmUserB)return;af("view_dms",{user_a:dmUserA,user_b:dmUserB}).then(setDmData);};
  const loadGroupChat=(code)=>{setSelGroupCode(code);af("view_group_chat",{group_code:code}).then(setGcData);};

  const sorted=useMemo(()=>{
    let f=[...users];
    if(filter==="active")f=f.filter(u=>u.lastSync&&new Date(u.lastSync)>new Date(Date.now()-7*86400000));
    if(filter==="inactive")f=f.filter(u=>!u.lastSync||new Date(u.lastSync)<new Date(Date.now()-30*86400000));
    if(filter==="photos")f=f.filter(u=>u.photos>0);
    if(filter==="vault")f=f.filter(u=>u.vaultPhotos>0);
    if(filter==="admin")f=f.filter(u=>u.isAdmin);
    if(search){const q=search.toLowerCase();f=f.filter(u=>u.name.toLowerCase().includes(q)||u.email.toLowerCase().includes(q)||(u.username||"").toLowerCase().includes(q));}
    f.sort((a,b)=>{let va=a[sort.key],vb=b[sort.key];
      if(typeof va==="string")return sort.dir==="asc"?(va||"").localeCompare(vb||""):(vb||"").localeCompare(va||"");
      return sort.dir==="asc"?(va||0)-(vb||0):(vb||0)-(va||0);});
    return f;
  },[users,sort,filter,search]);

  const toggleSort=(key)=>setSort(s2=>s2.key===key?{key,dir:s2.dir==="asc"?"desc":"asc"}:{key,dir:"desc"});
  const SortH=({k,l})=><button onClick={()=>toggleSort(k)} style={{background:"none",border:"none",padding:"4px 0",cursor:"pointer",
    fontSize:11,fontWeight:700,color:sort.key===k?V.accent:V.text3,fontFamily:V.font,textAlign:"left"}}>
    {l}{sort.key===k?(sort.dir==="asc"?" ↑":" ↓"):""}</button>;

  const ago2=(d)=>{if(!d)return"Never";const ms=Date.now()-new Date(d).getTime();const h=Math.floor(ms/3600000);
    if(h<1)return"Just now";if(h<24)return`${h}h ago`;const days=Math.floor(h/24);return days<7?`${days}d ago`:`${Math.floor(days/7)}w ago`;};

  // ═══ USER DETAIL ═══
  if(view==="detail"&&userData)return(
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      {/* Fullscreen photo viewer */}
      {enlargedPhoto&&(
        <div onClick={()=>setEnlargedPhoto(null)} style={{position:"fixed",top:0,left:0,right:0,bottom:0,zIndex:9999,
          background:"rgba(0,0,0,0.95)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:16,cursor:"pointer"}}>
          <button onClick={()=>setEnlargedPhoto(null)} style={{position:"absolute",top:16,right:16,background:track(),
            border:"none",borderRadius:10,width:36,height:36,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",zIndex:10}}>
            {Icons.x({size:18,color:"#fff"})}</button>
          {adminSrcOf(enlargedPhoto).src
            ?<img src={adminSrcOf(enlargedPhoto).src} alt="" style={{maxWidth:"100%",maxHeight:"80vh",objectFit:"contain",borderRadius:8}}/>
            :<div style={{color:"#888",padding:"40px",fontSize:13}}>{adminSrcOf(enlargedPhoto).state==="pending"?"Loading…":"No image available"}</div>}
          <div style={{marginTop:12,display:"flex",alignItems:"center",gap:10}}>
            <span style={{fontSize:12,color:"#fff"}}>{(enlargedPhoto.date||enlargedPhoto.created_at||"").slice(0,10)}</span>
            {enlargedPhoto.is_private&&<span style={{padding:"2px 8px",borderRadius:4,background:"rgba(168,85,247,0.3)",fontSize:11,color:"#fff"}}>🔒 Vault</span>}
          </div>
          {/* Prev/Next navigation */}
          {userData.photos?.length>1&&(()=>{
            const idx=userData.photos.findIndex(p=>(p.photo_id||p.id)===(enlargedPhoto.photo_id||enlargedPhoto.id));
            return(
              <div style={{display:"flex",gap:20,marginTop:12}}>
                {idx>0&&<button onClick={e=>{e.stopPropagation();setEnlargedPhoto(userData.photos[idx-1]);}} style={{padding:"8px 16px",borderRadius:8,
                  background:track(),border:"none",cursor:"pointer",color:"#fff",fontSize:12,fontWeight:600}}>← Prev</button>}
                <span style={{fontSize:11,color:"rgba(255,255,255,0.4)",alignSelf:"center"}}>{idx+1} / {userData.photos.length}</span>
                {idx<userData.photos.length-1&&<button onClick={e=>{e.stopPropagation();setEnlargedPhoto(userData.photos[idx+1]);}} style={{padding:"8px 16px",borderRadius:8,
                  background:track(),border:"none",cursor:"pointer",color:"#fff",fontSize:12,fontWeight:600}}>Next →</button>}
              </div>
            );
          })()}
        </div>
      )}
      <button onClick={()=>{setView("users");setUserData(null);setEnlargedPhoto(null);}} style={{background:"none",border:"none",display:"flex",alignItems:"center",gap:5,padding:"8px 0",cursor:"pointer"}}>
        {Icons.chevLeft({size:18,color:V.accent})}<span style={{fontSize:14,color:V.accent,fontWeight:600}}>Back</span></button>

      <Card style={{padding:14}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
          <div>
            <div style={{fontSize:16,fontWeight:800,color:V.text}}>{userData.profile?.first_name} {userData.profile?.last_name}</div>
            <div style={{fontSize:11,color:V.text3,fontFamily:V.mono}}>{selUser}</div>
            {userData.profile?.username&&<div style={{fontSize:11,color:V.accent}}>@{userData.profile.username}</div>}
          </div>
          {userData.profile?.is_admin&&<span style={{padding:"2px 8px",borderRadius:4,background:V.danger+"15",fontSize:11,fontWeight:700,color:V.danger}}>ADMIN</span>}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:6,marginTop:10}}>
          {[{l:"Workouts",v:userData.workouts?.length,c:V.accent},{l:"Nutrition",v:userData.nutrition?.length,c:V.warn},
            {l:"Photos",v:userData.photos?.length,c:V.accent2},{l:"Body",v:userData.body?.length,c:V.purple},
            {l:"Check-ins",v:userData.checkins?.length,c:"#ec4899"},{l:"Friends",v:userData.friends?.length,c:"#06b6d4"},
            {l:"Activity",v:userData.activity?.length,c:"#f97316"},{l:"Syncs",v:userData.syncHistory?.length,c:V.text3}
          ].map(st=><div key={st.l} style={{textAlign:"center",padding:6,borderRadius:6,background:V.card}}>
            <div style={{fontSize:14,fontWeight:800,color:st.c,fontFamily:V.mono}}>{st.v||0}</div><div style={{fontSize:11,color:V.text3}}>{st.l}</div></div>)}
        </div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4,marginTop:8,fontSize:11,color:V.text3}}>
          <div>Level: {userData.profile?.fitness_level||"—"}</div><div>Units: {userData.profile?.units||"lbs"}</div>
          {/* has_pin is a server-derived boolean — the API never sends the bcrypt hash.
              Fall back to the legacy field so an older API build still reads correctly. */}
          <div>PIN: {(userData.profile?.has_pin??!!userData.profile?.account_pin)?(userData.profile?.pin_locked?"Set · LOCKED":"Set"):"None"}</div>
          <div>Created: {userData.profile?.created_at?.slice(0,10)}</div>
          <div>Email: {userData.profile?.email_verified?"Verified":"Unverified"}</div>
          <div>Joined via: {userData.profile?.signup_app||"—"}</div>
        </div>
        <div style={{marginTop:10,display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
          {userData.profile?.is_banned?(
            <button onClick={()=>banUser(selUser,false)} disabled={banWorking}
              style={{padding:"7px 16px",borderRadius:8,background:V.accent+"15",border:`1px solid ${V.accent}40`,
                cursor:"pointer",fontSize:11,fontWeight:700,color:V.accent,fontFamily:V.font}}>
              {banWorking?"Working…":"✓ Unban User"}
            </button>
          ):(
            <button onClick={()=>ConfirmCtrl.show(`Ban ${selUser}?`,"Every active session is revoked immediately. Their data is kept and the ban can be lifted from this screen.",()=>banUser(selUser,true))} disabled={banWorking}
              style={{padding:"7px 16px",borderRadius:8,background:V.danger+"15",border:`1px solid ${V.danger}40`,
                cursor:"pointer",fontSize:11,fontWeight:700,color:V.danger,fontFamily:V.font}}>
              {banWorking?"Working…":"🚫 Ban User"}
            </button>
          )}
          {banResult&&<span style={{fontSize:11,fontWeight:600,color:banResult.ok?V.accent:V.danger}}>{banResult.msg}</span>}
        </div>
      </Card>

      {/* Organization (white-label): user_detail.org — `null` means solo, which is an answer */}
      <OrgContextCard org={userData.org} email={selUser} />

      {/* Gamification */}
      {userData.settings?.gamification&&(()=>{
        const g=userData.settings.gamification;
        const bonusXP=g.xp_bonus?.total||0;
        const lastKnownLevel=g.last_known_level||1;
        const rank=IRON_RANKS.find(r=>r.level===lastKnownLevel)||IRON_RANKS[0];
        const shields=g.streak_shields||0;
        const badgeDates=g.badge_dates||{};
        const badgeCount=Object.keys(badgeDates).length;
        const missionDays=Object.keys(g.missions_completed||{}).length;
        return(
          <Card style={{padding:12,border:`1px solid ${rank.color}20`,background:`${rank.color}04`}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
              <div style={{fontSize:11,fontWeight:700,color:V.text}}>Gamification</div>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <span style={{fontSize:14}}>{rank.icon}</span>
                <span style={{fontSize:11,fontWeight:800,color:rank.color}}>{rank.name}</span>
              </div>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:6}}>
              {[
                {l:"Bonus XP",v:bonusXP.toLocaleString(),c:V.accent},
                {l:"Level",v:lastKnownLevel,c:rank.color},
                {l:"Shields",v:shields,c:"#22c55e"},
                {l:"Badges",v:badgeCount,c:"#f59e0b"},
                {l:"Mission Days",v:missionDays,c:V.accent2},
                {l:"Duels",v:(g.duels||[]).length,c:V.purple},
                {l:"War Wins",v:g.war_wins||0,c:"#f43f5e"},
                {l:"Rivals",v:(g.rivals||[]).length,c:"#ec4899"},
              ].map(st=>(
                <div key={st.l} style={{textAlign:"center",padding:6,borderRadius:6,background:V.card}}>
                  <div style={{fontSize:13,fontWeight:800,color:st.c,fontFamily:V.mono}}>{st.v}</div>
                  <div style={{fontSize:11,color:V.text3}}>{st.l}</div>
                </div>
              ))}
            </div>
            {g.xp_bonus?.log?.length>0&&(
              <div style={{marginTop:8,maxHeight:80,overflowY:"auto"}}>
                {g.xp_bonus.log.slice(0,5).map((e2,i)=>(
                  <div key={i} style={{fontSize:11,color:V.text3,padding:"2px 0",borderBottom:`1px solid ${V.divider}`}}>
                    {e2.date} · <span style={{color:e2.amount>0?V.accent:V.danger}}>{e2.amount>0?"+":""}{e2.amount} XP</span> · {e2.reason}
                  </div>
                ))}
              </div>
            )}
          </Card>
        );
      })()}

      {/* Settings */}
      {userData.settings&&<Card style={{padding:12}}>
        <div style={{fontSize:11,fontWeight:700,color:V.text,marginBottom:4}}>Settings</div>
        <div style={{fontSize:11,color:V.text3,lineHeight:1.8}}>
          Goals: {userData.settings.goals?.cal||"—"}cal · {userData.settings.goals?.protein||"—"}P<br/>
          Schedule: {Object.values(userData.settings.schedule?.weekly||{}).join(", ")||"—"}<br/>
          Phases: {(userData.settings.phases||[]).length} · Injuries: {(userData.settings.injuries||[]).length} · Supps: {(userData.settings.supplements||[]).length}
        </div>
      </Card>}

      {/* Workouts */}
      <Card style={{padding:12}}>
        <div style={{fontSize:11,fontWeight:700,color:V.text,marginBottom:6}}>Workouts ({userData.workouts?.length})</div>
        <div style={{maxHeight:200,overflowY:"auto"}}>
          {(userData.workouts||[]).map((w,i)=><div key={i} style={{padding:"4px 0",borderBottom:`1px solid ${V.divider}`,fontSize:11,color:V.text2}}>
            <span style={{fontFamily:V.mono,color:V.accent}}>{w.date}</span> — {(w.exercises||[]).length} ex · {w.duration||"?"}min
            {(w.exercises||[]).length>0&&<span style={{color:V.text3}}> ({(w.exercises||[]).map(e=>e.exerciseId).join(", ")})</span>}
          </div>)}
        </div>
      </Card>

      {/* Body */}
      {userData.body?.length>0&&<Card style={{padding:12}}>
        <div style={{fontSize:11,fontWeight:700,color:V.text,marginBottom:6}}>Body ({userData.body.length})</div>
        {userData.body.slice(0,10).map((b,i)=><div key={i} style={{padding:"3px 0",fontSize:11,color:V.text3}}>
          {b.date} — {b.weight}{userData.profile?.units==="kg"?"kg":"lbs"} {b.body_fat?`· ${b.body_fat}%`:""} {b.chest?`· C:${b.chest}`:""} {b.waist?`W:${b.waist}`:""}
        </div>)}
      </Card>}

      {/* ALL Photos */}
      {userData.photos?.length>0&&<Card style={{padding:12}}>
        <div style={{fontSize:11,fontWeight:700,color:V.text,marginBottom:6}}>Photos ({userData.photos.length}) <span style={{color:V.danger,fontSize:11}}>all including vault</span></div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:4}}>
          {userData.photos.map((p,i)=><div key={i} onClick={()=>{if(adminSrcOf(p).src)setEnlargedPhoto(p);}} style={{position:"relative",borderRadius:6,overflow:"hidden",aspectRatio:"1",
            border:`1px solid ${p.is_private?V.purple+"40":V.cardBorder}`,cursor:adminSrcOf(p).src?"pointer":"default"}}>
            {adminSrcOf(p).src?<img loading="lazy" src={adminSrcOf(p).src} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/>
              :<div style={{width:"100%",height:"100%",background:V.card,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:V.text3}}>{adminSrcOf(p).state==="pending"?"…":"No URL"}</div>}
            {p.is_private&&<div style={{position:"absolute",top:1,right:1,padding:"0 3px",borderRadius:2,background:"rgba(168,85,247,0.8)",fontSize:6,color:"#fff"}}>🔒</div>}
            <div style={{position:"absolute",bottom:0,left:0,right:0,padding:"1px 2px",background:"rgba(0,0,0,0.7)",fontSize:6,color:"#fff"}}>{(p.date||p.created_at||"").slice(0,10)}</div>
          </div>)}
        </div>
      </Card>}

      {/* Check-ins */}
      {userData.checkins?.length>0&&<Card style={{padding:12}}>
        <div style={{fontSize:11,fontWeight:700,color:V.text,marginBottom:6}}>Check-ins ({userData.checkins.length})</div>
        {userData.checkins.slice(0,10).map((c,i)=><div key={i} style={{padding:"3px 0",fontSize:11,color:V.text3}}>
          {c.date} — Sleep:{c.sleep}h · Soreness:{c.soreness}/10 · Energy:{c.energy}/10 · Mood:{c.motivation}/10
        </div>)}
      </Card>}

      {/* Friends */}
      {userData.friends?.length>0&&<Card style={{padding:12}}>
        <div style={{fontSize:11,fontWeight:700,color:V.text,marginBottom:6}}>Friends ({userData.friends.length})</div>
        {userData.friends.map((f,i)=><div key={i} style={{padding:"3px 0",fontSize:11,display:"flex",justifyContent:"space-between"}}>
          <span style={{color:V.text2}}>{f.name}</span>
          <span style={{color:f.status==="accepted"?V.accent:V.warn}}>{f.status}</span>
        </div>)}
      </Card>}

      {/* Activity log */}
      {userData.activity?.length>0&&<Card style={{padding:12}}>
        <div style={{fontSize:11,fontWeight:700,color:V.text,marginBottom:6}}>Activity ({userData.activity.length})</div>
        <div style={{maxHeight:150,overflowY:"auto"}}>
          {userData.activity.map((a,i)=><div key={i} style={{padding:"3px 0",fontSize:11,color:V.text3,borderBottom:`1px solid ${V.divider}`}}>
            {a.created_at?.slice(0,16)} · <span style={{color:V.accent}}>{a.event_type}</span> · {JSON.stringify(a.event_data||{}).slice(0,60)}
          </div>)}
        </div>
      </Card>}

      {/* Sync history */}
      {userData.syncHistory?.length>0&&<Card style={{padding:12}}>
        <div style={{fontSize:11,fontWeight:700,color:V.text,marginBottom:6}}>Sync Log</div>
        {userData.syncHistory.map((l,i)=><div key={i} style={{padding:"2px 0",fontSize:11,color:V.text3}}>
          {l.synced_at?.slice(0,16)} · {l.device_id?.slice(0,12)} · {l.records_synced||"?"} records
        </div>)}
      </Card>}
    </div>
  );

  // ═══ MODERATION VIEW ═══
  if(view==="moderation")return(
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      <button onClick={()=>setView("dashboard")} style={{background:"none",border:"none",display:"flex",alignItems:"center",gap:5,padding:"8px 0",cursor:"pointer"}}>
        {Icons.chevLeft({size:18,color:V.accent})}<span style={{fontSize:14,color:V.accent,fontWeight:600}}>Back</span></button>
      <div style={{fontSize:16,fontWeight:800,color:V.text}}>Content Moderation</div>

      {/* DM Viewer */}
      <Card style={{padding:14}}>
        <div style={{fontSize:12,fontWeight:700,color:V.text,marginBottom:8}}>View DMs Between Users</div>
        {users.length===0&&<Btn v="secondary" full onClick={loadUsers} s={{marginBottom:8}}>Load Users</Btn>}
        {users.length>0&&(
          <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:8}}>
            <select value={dmUserA} onChange={e=>setDmUserA(e.target.value)}
              style={{padding:"10px 12px",background:V.bg,border:`1px solid ${V.cardBorder}`,borderRadius:8,color:V.text,fontSize:12,outline:"none",fontFamily:V.font}}>
              <option value="">Select User A...</option>
              {users.map((u,i)=><option key={i} value={u.email}>{u.name} (@{u.username||u.email.split("@")[0]})</option>)}
            </select>
            <select value={dmUserB} onChange={e=>setDmUserB(e.target.value)}
              style={{padding:"10px 12px",background:V.bg,border:`1px solid ${V.cardBorder}`,borderRadius:8,color:V.text,fontSize:12,outline:"none",fontFamily:V.font}}>
              <option value="">Select User B...</option>
              {users.filter(u=>u.email!==dmUserA).map((u,i)=><option key={i} value={u.email}>{u.name} (@{u.username||u.email.split("@")[0]})</option>)}
            </select>
          </div>
        )}
        <Btn v="secondary" full onClick={loadDMs} disabled={!dmUserA||!dmUserB}>Load DMs</Btn>
        {dmData&&(
          <div style={{marginTop:8,maxHeight:300,overflowY:"auto"}}>
            {dmData.messages?.length===0&&<div style={{fontSize:11,color:V.text3,padding:8}}>No messages found</div>}
            {(dmData.messages||[]).map((m,i)=><div key={i} style={{padding:"6px 0",borderBottom:`1px solid ${V.divider}`,fontSize:11}}>
              <span style={{color:V.accent,fontWeight:700}}>{m.name}</span>
              <span style={{color:V.text3}}> · {m.ts?.slice(0,16)}</span>
              <div style={{color:V.text2,marginTop:2}}>{m.text}</div>
            </div>)}
          </div>
        )}
      </Card>

      {/* Group Chat Viewer */}
      <Card style={{padding:14}}>
        <div style={{fontSize:12,fontWeight:700,color:V.text,marginBottom:8}}>View Group Chat</div>
        {groups.length===0&&<Btn v="secondary" full onClick={loadGroups} s={{marginBottom:8}}>Load Groups</Btn>}
        {groups.length>0&&(
          <div style={{display:"flex",gap:4,flexWrap:"wrap",marginBottom:8}}>
            {groups.map((g,i)=><button key={i} onClick={()=>loadGroupChat(g.code)} style={{padding:"4px 10px",borderRadius:6,
              background:selGroupCode===g.code?V.accent+"10":V.card,border:`1px solid ${selGroupCode===g.code?V.accent+"30":V.cardBorder}`,
              cursor:"pointer",fontSize:11,color:selGroupCode===g.code?V.accent:V.text3,fontFamily:V.font}}>{g.name} ({g.memberCount})</button>)}
          </div>
        )}
        {gcData&&(
          <div style={{maxHeight:300,overflowY:"auto"}}>
            {gcData.messages?.length===0&&<div style={{fontSize:11,color:V.text3,padding:8}}>No messages</div>}
            {(gcData.messages||[]).map((m,i)=><div key={i} style={{padding:"6px 0",borderBottom:`1px solid ${V.divider}`,fontSize:11}}>
              <span style={{color:V.purple,fontWeight:700}}>{m.name}</span>
              <span style={{color:V.text3}}> · {m.ts?.slice(0,16)}</span>
              <div style={{color:V.text2,marginTop:2}}>{m.text}</div>
            </div>)}
          </div>
        )}
      </Card>
    </div>
  );

  // ═══ USERS TABLE ═══
  if(view==="users")return(
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      <button onClick={()=>setView("dashboard")} style={{background:"none",border:"none",display:"flex",alignItems:"center",gap:5,padding:"8px 0",cursor:"pointer"}}>
        {Icons.chevLeft({size:18,color:V.accent})}<span style={{fontSize:14,color:V.accent,fontWeight:600}}>Back</span></button>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div style={{fontSize:16,fontWeight:800,color:V.text}}>Users ({sorted.length}{sorted.length!==users.length?` of ${users.length}`:""})</div>
        <Btn v="secondary" onClick={loadUsers} s={{fontSize:11}}>Refresh</Btn>
      </div>
      {/* Headline totals come from the API's exact count-only queries, not from summing the
          page — the per-user tallies below are read from a capped scan and say so. */}
      {userTotals&&(
        <div data-testid="admin-user-totals" style={{fontSize:11,color:V.text3}}>
          Platform totals: <b style={{color:V.accent}}>{num(userTotals.workouts)}</b> workouts · <b style={{color:V.accent2}}>{num(userTotals.photos)}</b> photos · <b style={{color:V.purple}}>{num(userTotals.vaultPhotos)}</b> vault
        </div>
      )}
      {usersTruncated&&(
        <div data-testid="admin-users-truncated" style={{fontSize:11,color:V.warn,lineHeight:1.5}}>
          The per-user workout and photo counts below hit the API's row cap, so they under-count for the busiest accounts. The platform totals above are still exact.
        </div>
      )}
      {orgMap&&orgMap.state!=="loading"&&(orgMap.note||orgMap.state==="denied"||orgMap.state==="error")&&(
        <div data-testid="admin-users-org-note" style={{fontSize:11,color:orgMap.state==="ready"?V.text3:V.warn,lineHeight:1.5}}>{orgMap.note}</div>
      )}
      <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search name, email, username..."
        style={{padding:"10px 14px",background:V.card,border:`1px solid ${V.cardBorder}`,borderRadius:10,color:V.text,fontSize:12,outline:"none",fontFamily:V.font}}/>
      <div style={{display:"flex",gap:4,flexWrap:"wrap"}}>
        {[{id:"all",l:"All"},{id:"active",l:"Active 7d"},{id:"inactive",l:"Inactive 30d"},{id:"photos",l:"Has Photos"},{id:"vault",l:"Has Vault"},{id:"admin",l:"Admins"}].map(f2=>(
          <button key={f2.id} onClick={()=>setFilter(f2.id)} style={{padding:"4px 10px",borderRadius:6,
            background:filter===f2.id?V.accent+"10":V.card,border:`1px solid ${filter===f2.id?V.accent+"30":V.cardBorder}`,
            cursor:"pointer",fontSize:11,fontWeight:600,color:filter===f2.id?V.accent:V.text3,fontFamily:V.font}}>{f2.l}</button>
        ))}
      </div>
      {/* Sort headers */}
      <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",gap:4,padding:"0 4px"}}>
        <SortH k="name" l="NAME"/><SortH k="workouts" l="WORK"/><SortH k="photos" l="PHOTOS"/><SortH k="created" l="JOINED"/>
      </div>
      {loading&&<div><SkeletonCard lines={2}/><div style={{height:8}}/><SkeletonCard lines={2}/><div style={{height:8}}/><SkeletonCard lines={2}/></div>}
      {sorted.map((u,i)=>(
        <Card key={i} style={{padding:10,cursor:"pointer"}} onClick={()=>loadDetail(u.email)}>
          <div style={{display:"grid",gridTemplateColumns:"2fr 1fr 1fr 1fr",gap:4,alignItems:"center"}}>
            <div style={{minWidth:0}}>
              <div style={{fontSize:12,fontWeight:600,color:V.text}}>{u.name} {u.isAdmin&&<span style={{fontSize:11,color:V.danger}}>⭐</span>}</div>
              <div style={{fontSize:11,color:V.text3}}>@{u.username||"—"} · {ago2(u.lastSync)}</div>
              <OrgBadge org={orgOf(u.email)} known={orgMap?.state==="ready"}/>
            </div>
            <div style={{fontSize:12,fontWeight:700,color:V.accent,fontFamily:V.mono,textAlign:"center"}}>{u.workouts}</div>
            <div style={{textAlign:"center"}}>
              <span style={{fontSize:12,fontWeight:700,color:V.accent2,fontFamily:V.mono}}>{u.photos}</span>
              {u.vaultPhotos>0&&<span style={{fontSize:11,color:V.purple}}> +{u.vaultPhotos}🔒</span>}
            </div>
            <div style={{fontSize:11,color:V.text3,textAlign:"right"}}>{u.created?.slice(0,10)}</div>
          </div>
        </Card>
      ))}
    </div>
  );

  // ═══ USER LOOKUP ═══
  if(view==="lookup")return(
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      <div style={{fontSize:16,fontWeight:800,color:V.text}}>User Lookup</div>
      <div style={{fontSize:11,color:V.text3}}>Search by name, email, or username — tap to view full profile</div>
      <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search..." autoFocus
        style={{padding:"12px 14px",background:V.card,border:`1px solid ${V.cardBorder}`,borderRadius:12,color:V.text,fontSize:14,outline:"none",fontFamily:V.font}}/>
      {loading&&<div><SkeletonCard lines={2}/><div style={{height:8}}/><SkeletonCard lines={2}/></div>}
      {search&&sorted.slice(0,15).map((u,i)=>(
        <Card key={i} style={{padding:12,cursor:"pointer"}} onClick={()=>loadDetail(u.email)}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <div>
              <div style={{fontSize:13,fontWeight:600,color:V.text}}>{u.name} {u.isAdmin&&<span style={{fontSize:11,color:V.danger}}>ADMIN</span>}</div>
              <div style={{fontSize:11,color:V.text3,fontFamily:V.mono}}>{u.email}</div>
              {u.username&&<div style={{fontSize:11,color:V.accent}}>@{u.username}</div>}
              <OrgBadge org={orgOf(u.email)} known={orgMap?.state==="ready"}/>
            </div>
            <div style={{textAlign:"right"}}>
              <div style={{fontSize:14,fontWeight:800,color:V.accent,fontFamily:V.mono}}>{u.workouts}</div>
              <div style={{fontSize:11,color:V.text3}}>workouts</div>
              {u.photos>0&&<div style={{fontSize:11,color:V.accent2}}>{u.photos} 📸{u.vaultPhotos>0?` +${u.vaultPhotos}🔒`:""}</div>}
            </div>
          </div>
        </Card>
      ))}
      {search&&sorted.length===0&&!loading&&<div style={{textAlign:"center",padding:20,fontSize:12,color:V.text3}}>No users found</div>}
      {!search&&<div style={{textAlign:"center",padding:30,fontSize:12,color:V.text3}}>Type to search...</div>}
    </div>
  );

  // ═══ PLATFORM HEALTH ═══
  if(view==="health")return(
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      <div style={{fontSize:16,fontWeight:800,color:V.text}}>Platform Health</div>
      {!health&&<Btn full onClick={loadHealth}>Load Health Data</Btn>}
      {health&&(<>
        <Card style={{padding:14}}>
          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
            <div style={{textAlign:"center",padding:8,borderRadius:8,background:V.card}}>
              <div style={{fontSize:20,fontWeight:800,color:V.accent,fontFamily:V.mono}}>{health.totalActive}</div>
              <div style={{fontSize:11,color:V.text3}}>ACTIVE SESSIONS</div>
            </div>
            <div style={{textAlign:"center",padding:8,borderRadius:8,background:V.card}}>
              <div style={{fontSize:20,fontWeight:800,color:V.warn,fontFamily:V.mono}}>{health.staleUsers}</div>
              <div style={{fontSize:11,color:V.text3}}>STALE (30d+)</div>
            </div>
            <div style={{textAlign:"center",padding:8,borderRadius:8,background:V.card}}>
              <div style={{fontSize:20,fontWeight:800,color:V.danger,fontFamily:V.mono}}>{health.activeNotSyncing}</div>
              <div style={{fontSize:11,color:V.text3}}>NOT SYNCING</div>
            </div>
          </div>
        </Card>
        {health.whitelabel&&<WhiteLabelHealth wl={health.whitelabel}/>}
        {health.staleList?.length>0&&(
          <Card style={{padding:12}}>
            <div style={{fontSize:11,fontWeight:700,color:V.warn,marginBottom:6}}>Stale Users (no sync in 30+ days)</div>
            {health.staleList.map((e,i)=><div key={i} style={{padding:"3px 0",fontSize:11,color:V.text3,fontFamily:V.mono,
              borderBottom:`1px solid ${V.divider}`,cursor:"pointer"}} onClick={()=>loadDetail(e)}>{e}</div>)}
          </Card>
        )}
        {health.notSyncingList?.length>0&&(
          <Card style={{padding:12}}>
            <div style={{fontSize:11,fontWeight:700,color:V.danger,marginBottom:6}}>Active but Not Syncing (session active, no sync 7d)</div>
            {health.notSyncingList.map((e,i)=><div key={i} style={{padding:"3px 0",fontSize:11,color:V.text3,fontFamily:V.mono,
              borderBottom:`1px solid ${V.divider}`,cursor:"pointer"}} onClick={()=>loadDetail(e)}>{e}</div>)}
          </Card>
        )}
        <Btn v="secondary" full onClick={loadHealth}>Refresh</Btn>
      </>)}
    </div>
  );

  // ═══ DASHBOARD ═══
  return(
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div style={{display:"flex",alignItems:"center",gap:8}}>
        <span style={{fontSize:18}}>🔐</span>
        <div>
          <div style={{fontSize:16,fontWeight:800,color:V.text}}>Admin Panel</div>
          <div style={{fontSize:11,color:V.danger}}>Server-verified · {s.profile?.email}</div>
        </div>
      </div>

      {dash&&(<>
        <Card style={{padding:14}}>
          <div style={{fontSize:12,fontWeight:700,color:V.text,marginBottom:8}}>Platform Overview</div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6}}>
            {[{l:"TOTAL USERS",v:dash.totalUsers,c:V.accent},{l:"ACTIVE (7d)",v:dash.activeUsers7d,c:"#22d3ee"},
              {l:"NEW (7d)",v:dash.newUsers7d,c:"#ec4899"},
              {l:"WORKOUTS",v:dash.totalWorkouts,c:V.warn},{l:"TODAY",v:dash.workoutsToday,c:V.accent},
              {l:"THIS WEEK",v:dash.workouts7d,c:V.accent2},
              {l:"NUTRITION",v:dash.totalNutrition,c:"#f97316"},{l:"PHOTOS",v:dash.totalPhotos,c:V.purple},
              {l:"SESSIONS",v:dash.activeSessions,c:V.text3}
            ].map(st=><div key={st.l} style={{textAlign:"center",padding:6,borderRadius:6,background:V.card}}>
              <div style={{fontSize:16,fontWeight:800,color:st.c,fontFamily:V.mono}}>{st.v}</div>
              <div style={{fontSize:11,color:V.text3}}>{st.l}</div>
            </div>)}
          </div>
        </Card>

        {dash.whitelabel&&<WhiteLabelDash wl={dash.whitelabel} adminEmailConfigured={adminEmailConfigured}/>}

        {dash.chartData&&<Card style={{padding:12}}>
          <div style={{fontSize:11,fontWeight:700,color:V.text,marginBottom:8}}>14-Day Trend</div>
          <ResponsiveContainer width="100%" height={120}>
            <BarChart data={dash.chartData} barSize={6}>
              <CartesianGrid {...chartCfg.grid} vertical={false}/>
              <XAxis dataKey="date" {...chartCfg.axis} interval={2}/>
              <YAxis {...chartCfg.axis} width={20}/>
              <Tooltip {...chartCfg.tip}/>
              <Bar dataKey="workouts" fill={V.accent} radius={[2,2,0,0]}/>
              <Bar dataKey="signups" fill="#ec4899" radius={[2,2,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
          <div style={{display:"flex",gap:12,justifyContent:"center",marginTop:4}}>
            <span style={{fontSize:11,color:V.accent}}>■ Workouts</span>
            <span style={{fontSize:11,color:"#ec4899"}}>■ Signups</span>
          </div>
        </Card>}
      </>)}

      {/* Health */}
      {health&&<Card style={{padding:14}}>
        <div style={{fontSize:12,fontWeight:700,color:V.text,marginBottom:6}}>Platform Health</div>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:8}}>
          <div style={{textAlign:"center"}}><div style={{fontSize:16,fontWeight:800,color:V.accent,fontFamily:V.mono}}>{health.totalActive}</div><div style={{fontSize:11,color:V.text3}}>Active Sessions</div></div>
          <div style={{textAlign:"center"}}><div style={{fontSize:16,fontWeight:800,color:V.warn,fontFamily:V.mono}}>{health.staleUsers}</div><div style={{fontSize:11,color:V.text3}}>Stale (30d+)</div></div>
          <div style={{textAlign:"center"}}><div style={{fontSize:16,fontWeight:800,color:V.danger,fontFamily:V.mono}}>{health.activeNotSyncing}</div><div style={{fontSize:11,color:V.text3}}>Not Syncing</div></div>
        </div>
        {health.notSyncingList?.length>0&&<div style={{fontSize:11,color:V.text3,marginTop:6}}>Not syncing: {health.notSyncingList.slice(0,5).join(", ")}</div>}
        {health.whitelabel&&(
          <div data-testid="dash-wl-health" style={{fontSize:11,marginTop:6,color:severityColor(health.whitelabel.worst)}}>
            White-label: {severityLabel(health.whitelabel.worst)} — {cronStatus(health.whitelabel.cron).label}
          </div>
        )}
      </Card>}

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
        <Btn full onClick={()=>{loadUsers();setView("users");}}>{Icons.target({size:14,color:onAccent()})} Users</Btn>
        <Btn full onClick={()=>{loadGroups();setView("moderation");}} s={{background:V.purple}}>{Icons.activity({size:14,color:V.bg})} Moderation</Btn>
      </div>
      <Btn v="secondary" full onClick={()=>{loadHealth();af("dashboard").then(setDash);}}>Refresh All</Btn>
    </div>
  );
}


export function DataManager({s,d}){
  const fileRef=useRef(null);
  const [msg,setMsg]=useState(null);
  const [restoring,setRestoring]=useState(false);
  const lastSync=LS.get("ft-last-sync");

  const exportData=()=>{
    const data=buildBackupPayload(s);
    const blob=new Blob([JSON.stringify(data,null,2)],{type:"application/json"});
    const url=URL.createObjectURL(blob);
    const a=document.createElement("a");a.href=url;a.download=`ironlog-backup-${today()}.json`;a.click();
    setTimeout(()=>URL.revokeObjectURL(url),1000); // Safari can abort the download if revoked synchronously
    setMsg({type:"success",text:`Exported ${s.workouts.length} workouts, ${s.nutrition.length} nutrition logs, ${s.body.length} measurements`});
    setTimeout(()=>setMsg(null),4000);
  };

  const importData=(e)=>{
    const file=e.target.files?.[0];
    if(!file)return;
    const reader=new FileReader();
    reader.onload=(ev)=>{
      try{
        const raw=JSON.parse(ev.target.result);
        const data=normalizeBackupHealth({...raw});delete data._ironlog;
        // A backup recorded in the other unit is converted into the current one so history isn't mixed
        if((data.units==="lbs"||data.units==="kg")&&data.units!==s.units){
          const conv=convertStateUnits({workouts:data.workouts||[],body:data.body||[],goals:data.goals||{},
            exercises:[...s.exercises,...(data.exercises||[])]},data.units,s.units);
          data.workouts=conv.workouts;data.body=conv.body;
          if(data.goals)data.goals=conv.goals;
          data.units=s.units;
        }
        const existingWIds=new Set(s.workouts.map(w=>w.id));
        const existingNIds=new Set(s.nutrition.map(n=>n.id));
        const existingBIds=new Set(s.body.map(b=>b.id));
        const newW=(data.workouts||[]).filter(w=>!existingWIds.has(w.id)).length;
        const newN=(data.nutrition||[]).filter(n=>!existingNIds.has(n.id)).length;
        const newB=(data.body||[]).filter(b=>!existingBIds.has(b.id)).length;
        const merged={
          ...data,
          workouts:[...s.workouts,...(data.workouts||[]).filter(w=>!existingWIds.has(w.id))],
          nutrition:[...s.nutrition,...(data.nutrition||[]).filter(n=>!existingNIds.has(n.id))],
          body:[...s.body,...(data.body||[]).filter(b=>!existingBIds.has(b.id))],
          exercises:[...s.exercises,...(data.exercises||[]).filter(e=>!s.exercises.some(x=>x.id===e.id))],
        };
        d({type:"IMPORT",data:merged});
        // Restore gamification LS keys if present in backup
        const gKeys={xp_bonus:"ft-xp-bonus",missions_completed:"ft-missions-completed",streak_shields:"ft-streak-shields",
          shield_used_date:"ft-shield-used-date",shield_awarded_streak:"ft-shield-awarded-streak",comeback_used:"ft-comeback-used",
          last_known_level:"ft-last-known-level",duels:"ft-duels",rivals:"ft-rivals",war_wins:"ft-war-wins",war_streak:"ft-war-streak"};
        restoreWarState(LS,data);
        Object.entries(gKeys).forEach(([bKey,lsKey])=>{if(data[bKey]!=null)LS.set(lsKey,data[bKey]);});
        setMsg({type:"success",text:`Imported! +${newW} workouts, +${newN} nutrition, +${newB} measurements`});
        setTimeout(()=>setMsg(null),5000);
      }catch(err){
        setMsg({type:"error",text:"Invalid backup file. Make sure it's an IRONLOG JSON export."});
        setTimeout(()=>setMsg(null),4000);
      }
    };
    reader.readAsText(file);
    e.target.value="";
  };

  const [restorePin,setRestorePin]=useState("");
  const [showRestorePin,setShowRestorePin]=useState(false);

  const restoreFromCloud=async(pinOverride)=>{
    const email=s.profile?.email;
    if(!email){setMsg({type:"error",text:"No email found. Complete your profile first."});setTimeout(()=>setMsg(null),4000);return;}
    setRestoring(true);
    const pin=pinOverride||restorePin||(await LS.getSecure("ft-account-pin"))||null;
    const result=await CloudSync.pull(email,LS.get("ft-device-id"),pin);

    if(result?.pin_required){
      setRestoring(false);setShowRestorePin(true);
      setMsg({type:"info",text:"Enter your account PIN to restore data."});
      return;
    }
    if(result?.wrong_pin){
      setRestoring(false);setRestorePin("");
      setMsg({type:"error",text:`Wrong PIN. ${result.attempts_remaining} attempts remaining.${result.locked?" Account locked for 15 minutes.":""}`});
      setTimeout(()=>setMsg(null),5000);return;
    }
    if(result?.locked){
      setRestoring(false);
      setMsg({type:"error",text:`Account locked. Try again in ${result.retry_minutes} minutes.`});
      setTimeout(()=>setMsg(null),5000);return;
    }

    if(result&&result.success){
      setShowRestorePin(false);setRestorePin("");
      const cloud=result.data;
      // Smart merge cloud data into local
      const existingWIds=new Set(s.workouts.map(w=>w.id));
      const existingNIds=new Set(s.nutrition.map(n=>n.id));
      const existingBIds=new Set(s.body.map(b=>b.id));
      const newW=(cloud.workouts||[]).filter(w=>!existingWIds.has(w.id));
      const newN=(cloud.nutrition||[]).filter(n=>!existingNIds.has(n.id));
      const newB=(cloud.body||[]).filter(b=>!existingBIds.has(b.id));
      const newP=(cloud.photos||[]).filter(p=>!s.photos.some(x=>x.id===p.id));
      const existingCheckinDates=new Set((s.checkins||[]).map(c=>c.date));
      const newC=(cloud.checkins||[]).filter(c=>!c.deleted&&!existingCheckinDates.has(c.date));
      const existingMIds=new Set((s.milestones||[]).map(m=>m.id));
      const newM=(cloud.milestones||[]).filter(m=>!existingMIds.has(m.id));

      const merged={
        workouts:[...s.workouts,...newW].sort((a,b)=>b.date.localeCompare(a.date)),
        nutrition:[...s.nutrition,...newN].sort((a,b)=>b.date.localeCompare(a.date)),
        body:[...s.body,...newB].sort((a,b)=>b.date.localeCompare(a.date)),
        photos:[...s.photos,...newP],
        checkins:[...(s.checkins||[]),...newC],
        milestones:[...(s.milestones||[]),...newM],
        exercises:cloud.exercises||s.exercises,
        goals:cloud.goals||s.goals,
        activeProgram:activeProgramFromWire(cloud,s.activeProgram),
        schedule:cloud.schedule||s.schedule,
        units:cloud.units||s.units,
        profile:cloud.profile||s.profile,
      };
      d({type:"IMPORT",data:merged});
      // Restore vault PIN from cloud if it exists
      // vault_pin no longer restored from server (kept local-only)
      if(cloud.phases)d({type:"SET_PHASES",phases:cloud.phases});
      if(cloud.injuries)d({type:"SET_INJURIES",injuries:cloud.injuries});
      if(cloud.privacy)LS.set("ft-privacy",cloud.privacy);
      if(cloud.supplements)LS.set("ft-supplements",cloud.supplements);
    if(cloud.accountability)LS.set("ft-accountability",cloud.accountability);
      // Restore gamification
      if(cloud.gamification){
        const g=cloud.gamification;
        const localBonus=LS.get("ft-xp-bonus")||{total:0,log:[]};
        if((g.xp_bonus?.total||0)>(localBonus.total||0))LS.set("ft-xp-bonus",g.xp_bonus);
        const localMissions=LS.get("ft-missions-completed")||{};
        LS.set("ft-missions-completed",{...g.missions_completed,...localMissions});
        const localShields=LS.get("ft-streak-shields")||0;
        if((g.streak_shields||0)>localShields)LS.set("ft-streak-shields",g.streak_shields);
        if(g.shield_awarded_streak)LS.set("ft-shield-awarded-streak",g.shield_awarded_streak);
        const localLevel=LS.get("ft-last-known-level")||1;
        if((g.last_known_level||1)>localLevel)LS.set("ft-last-known-level",g.last_known_level);
        const localBadgeDates=LS.get("ft-badge-dates")||{};
        const mergedBD={...g.badge_dates};
        for(const[k,v]of Object.entries(localBadgeDates)){if(!mergedBD[k]||v<mergedBD[k])mergedBD[k]=v;}
        LS.set("ft-badge-dates",mergedBD);
        if(g.duels?.length>0&&!(LS.get("ft-duels")?.length>0))LS.set("ft-duels",g.duels);
        if(g.rivals?.length>0&&!(LS.get("ft-rivals")?.length>0))LS.set("ft-rivals",g.rivals);
        if(g.war_wins>0&&!LS.get("ft-war-wins"))LS.set("ft-war-wins",g.war_wins);
        if(g.war_streak>0&&!LS.get("ft-war-streak"))LS.set("ft-war-streak",g.war_streak);
        if(g.shield_used_date&&!LS.get("ft-shield-used-date"))LS.set("ft-shield-used-date",g.shield_used_date);
        if(g.comeback_used&&!LS.get("ft-comeback-used"))LS.set("ft-comeback-used",g.comeback_used);
        if(g.duel_record){const local=LS.get("ft-duel-record")||{wins:0,losses:0,ties:0};
          if((g.duel_record.wins||0)>(local.wins||0))LS.set("ft-duel-record",g.duel_record);}
      }
      setMsg({type:"success",text:`Restored from cloud! +${newW.length} workouts, +${newN.length} nutrition, +${newB.length} measurements, +${newC.length} check-ins, +${newM.length} goals`});
    }else{
      setMsg({type:"error",text:"No cloud backup found for this account, or server is unreachable."});
    }
    setRestoring(false);
    setTimeout(()=>setMsg(null),5000);
  };

  return(
    <div style={{display:"flex",flexDirection:"column",gap:10}}>
      {msg&&(
        <div style={{padding:"10px 14px",borderRadius:10,fontSize:12,fontWeight:600,
          background:`${msg.type==="success"?V.accent:msg.type==="warn"?V.warn:msg.type==="info"?V.accent2:V.danger}12`,
          color:msg.type==="success"?V.accent:msg.type==="warn"?V.warn:msg.type==="info"?V.accent2:V.danger,
          border:`1px solid ${msg.type==="success"?V.accent:msg.type==="warn"?V.warn:msg.type==="info"?V.accent2:V.danger}20`}}>
          {msg.text}
        </div>
      )}

      {/* Sync status */}
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"8px 12px",
        background:V.card,borderRadius:8,border:`1px solid ${V.cardBorder}`}}>
        <div>
          <div style={{fontSize:11,color:V.text3}}>Cloud Sync</div>
          <div style={{fontSize:11,color:s.profile?.email?V.accent:V.text3,fontFamily:V.mono}}>
            {s.profile?.email?`Linked: ${s.profile.email}`:"Not linked — complete profile"}
          </div>
        </div>
        {lastSync&&(
          <div style={{fontSize:11,color:V.text3,textAlign:"right"}}>
            Last sync<br/><span style={{fontFamily:V.mono}}>{new Date(lastSync).toLocaleString()}</span>
          </div>
        )}
      </div>

      <div style={{fontSize:11,color:V.text3,marginBottom:2}}>
        {s.workouts.length} workouts · {s.nutrition.length} nutrition logs · {s.body.length} measurements
      </div>

      {/* Cloud restore */}
      {showRestorePin&&(
        <div style={{padding:14,borderRadius:10,background:V.card,border:`1px solid ${V.cardBorder}`,marginBottom:6}}>
          <div style={{fontSize:12,fontWeight:700,color:V.text,marginBottom:4}}>Account PIN required</div>
          <div style={{fontSize:11,color:V.text3,marginBottom:8}}>Enter your 6-digit PIN to access cloud data.</div>
          <div style={{display:"flex",gap:8}}>
            <input type="password" inputMode="numeric" value={restorePin} onChange={e=>setRestorePin(e.target.value.replace(/\D/g,"").slice(0,6))}
              placeholder="• • • • • •" maxLength={6} autoComplete="current-password"
              onKeyDown={e=>{if(e.key==="Enter"&&restorePin.length===6)restoreFromCloud(restorePin);}}
              style={{flex:1,padding:"12px",background:V.inputBg,border:`1px solid ${V.cardBorder}`,
                borderRadius:10,color:V.text,fontSize:20,textAlign:"center",fontFamily:V.mono,letterSpacing:10,outline:"none"}}/>
            <Btn onClick={()=>restoreFromCloud(restorePin)} disabled={restorePin.length!==6||restoring}>
              {restoring?"...":"Go"}
            </Btn>
          </div>
        </div>
      )}
      <Btn full onClick={()=>restoreFromCloud()} disabled={restoring}>
        {restoring?(
          <span style={{display:"flex",alignItems:"center",gap:8,justifyContent:"center"}}>
            <div style={{width:14,height:14,borderRadius:7,border:"2px solid rgba(255,255,255,0.2)",
              borderTopColor:V.bg,animation:"spin 0.8s linear infinite"}}/>
            Restoring...
          </span>
        ):(
          <span>{Icons.download({size:16,color:V.bg})} Restore from Cloud</span>
        )}
      </Btn>
      <div style={{fontSize:11,color:V.text3}}>Pull your data from the server. Use this on a new device to get all your workouts, nutrition, and measurements back.</div>

      {/* Local export/import */}
      <div style={{borderTop:`1px solid ${V.cardBorder}`,paddingTop:10,marginTop:4}}>
        <div style={{fontSize:11,fontWeight:600,color:V.text3,marginBottom:8}}>Local Backup</div>
        <Btn v="secondary" full onClick={exportData}>{Icons.download({size:14,color:V.text2})} Export JSON</Btn>
        <input ref={fileRef} type="file" accept=".json" onChange={importData} style={{display:"none"}}/>
        <Btn v="secondary" full onClick={()=>fileRef.current?.click()} s={{marginTop:6}}>{Icons.upload({size:14,color:V.text2})} Import JSON</Btn>
        <div style={{fontSize:11,color:V.text3,marginTop:4}}>Import merges data — your existing entries won't be duplicated or lost.</div>
      </div>

      {/* #4 Cloud vs Local Parity Checker */}
      <div style={{borderTop:`1px solid ${V.cardBorder}`,paddingTop:10,marginTop:4}}>
        <div style={{fontSize:11,fontWeight:600,color:V.text3,marginBottom:8}}>Data Health Check</div>
        <Btn v="secondary" full onClick={async()=>{
          const email=s.profile?.email;
          if(!email){setMsg({type:"error",text:"No email — complete your profile first."});setTimeout(()=>setMsg(null),4000);return;}
          setMsg({type:"info",text:"Checking cloud parity..."});
          try{
            // Try with session token first, then with stored PIN
            const pin=(await LS.getSecure("ft-account-pin"))||null;
            const result=await CloudSync.pull(email,LS.get("ft-device-id"),pin);

            if(result?.pin_required){
              setMsg({type:"error",text:"PIN required for cloud access. Use Restore from Cloud below."});
              setTimeout(()=>setMsg(null),5000);return;
            }
            if(!result||!result.success){
              setMsg({type:"error",text:result?.error||"Could not reach cloud. Try again later."});
              setTimeout(()=>setMsg(null),4000);return;
            }
            const cloud=result.data;
            const report=[];
            const checks=[
              {name:"Workouts",local:s.workouts.length,cloud:(cloud.workouts||[]).length},
              {name:"Nutrition",local:s.nutrition.length,cloud:(cloud.nutrition||[]).length},
              {name:"Body",local:s.body.length,cloud:(cloud.body||[]).length},
              {name:"Photos",local:(s.photos||[]).length,cloud:(cloud.photos||[]).length},
              {name:"Check-ins",local:(s.checkins||[]).length,cloud:(cloud.checkins||[]).length},
              {name:"Goals",local:(s.milestones||[]).length,cloud:(cloud.milestones||[]).length},
            ];
            let allMatch=true;
            checks.forEach(c=>{
              if(c.local!==c.cloud){allMatch=false;report.push(`${c.name}: ${c.local} local / ${c.cloud} cloud`);}
            });
            const cloudWIds=new Set((cloud.workouts||[]).map(w=>w.id));
            const unsyncedW=s.workouts.filter(w=>!cloudWIds.has(w.id)).length;
            if(unsyncedW>0){allMatch=false;report.push(`${unsyncedW} workout(s) not yet in cloud`);}
            const cloudNIds=new Set((cloud.nutrition||[]).map(n=>n.id));
            const unsyncedN=s.nutrition.filter(n=>!cloudNIds.has(n.id)).length;
            if(unsyncedN>0){allMatch=false;report.push(`${unsyncedN} nutrition log(s) not yet in cloud`);}

            if(allMatch){
              setMsg({type:"success",text:`✅ All in sync — ${s.workouts.length} workouts, ${s.nutrition.length} nutrition, ${s.body.length} body`});
            }else{
              setMsg({type:"warn",text:`⚠️ Differences:\n${report.join("\n")}`});
            }
            setTimeout(()=>setMsg(null),10000);
          }catch(e){
            setMsg({type:"error",text:"Health check failed: "+e.message});
            setTimeout(()=>setMsg(null),4000);
          }
        }}>{Icons.activity({size:14,color:V.text2})} Run Health Check</Btn>
        <div style={{fontSize:11,color:V.text3,marginTop:4}}>Compare local data against cloud to find unsynced entries.</div>
        {msg&&msg.text?.includes("Differences")&&(
          <Btn v="secondary" full onClick={async()=>{
            setMsg({type:"info",text:"Pushing all local data to cloud..."});
            try{
              await CloudSync.push(s);
              setMsg({type:"success",text:"✅ Sync complete!"});
            }catch(e){
              setMsg({type:"error",text:"Sync failed — check connection"});
            }
            setTimeout(()=>setMsg(null),4000);
          }} s={{marginTop:6}}>
            {Icons.refresh({size:14,color:V.text2})} Force Sync Now
          </Btn>
        )}
      </div>
    </div>
  );
}

// ─── Exercise → Muscle Mapping ───
export function PhotoCompare({s}){
  // Compare works on the locally held image only (base64): it needs both halves at once and
  // must work offline, and a stored remote URL is never the durable copy of a photo.
  const photos=(s.photos||[]).filter(p=>!p.private&&localSrc(p)).sort((a,b)=>a.date.localeCompare(b.date));
  const [leftIdx,setLeftIdx]=useState(0);
  const [rightIdx,setRightIdx]=useState(Math.max(0,photos.length-1));
  const [sliderPos,setSliderPos]=useState(50);
  const containerRef=useRef(null);

  if(photos.length<2)return(
    <div style={{textAlign:"center",padding:40}}>
      <div style={{fontSize:32,marginBottom:8}}>📸</div>
      <div style={{fontSize:14,fontWeight:700,color:V.text}}>Need at least 2 photos</div>
      <div style={{fontSize:11,color:V.text3,marginTop:4}}>Take progress photos over time to compare your transformation.</div>
    </div>
  );

  const handleSlide=(e)=>{
    const rect=containerRef.current?.getBoundingClientRect();
    if(!rect)return;
    const clientX=e.touches?e.touches[0].clientX:e.clientX;
    const pct=Math.max(5,Math.min(95,((clientX-rect.left)/rect.width)*100));
    setSliderPos(pct);
  };

  return(
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div style={{fontSize:16,fontWeight:800,color:V.text}}>Progress Compare</div>
      {/* Date selectors */}
      <div style={{display:"flex",gap:8}}>
        <div style={{flex:1}}>
          <div style={{fontSize:11,color:V.text3,marginBottom:4,fontWeight:600}}>BEFORE</div>
          <select value={leftIdx} onChange={e=>setLeftIdx(parseInt(e.target.value))}
            style={{width:"100%",padding:"8px 10px",background:V.inputBg,border:`1px solid ${V.cardBorder}`,
              borderRadius:8,color:V.text,fontSize:12,outline:"none",fontFamily:V.font}}>
            {photos.map((p,i)=><option key={i} value={i} style={{background:V.bg}}>{fmtShort(p.date)}</option>)}
          </select>
        </div>
        <div style={{flex:1}}>
          <div style={{fontSize:11,color:V.text3,marginBottom:4,fontWeight:600}}>AFTER</div>
          <select value={rightIdx} onChange={e=>setRightIdx(parseInt(e.target.value))}
            style={{width:"100%",padding:"8px 10px",background:V.inputBg,border:`1px solid ${V.cardBorder}`,
              borderRadius:8,color:V.text,fontSize:12,outline:"none",fontFamily:V.font}}>
            {photos.map((p,i)=><option key={i} value={i} style={{background:V.bg}}>{fmtShort(p.date)}</option>)}
          </select>
        </div>
      </div>
      {/* Time span */}
      {photos[leftIdx]&&photos[rightIdx]&&(()=>{
        const d1=new Date(photos[leftIdx].date),d2=new Date(photos[rightIdx].date);
        const days=Math.abs(Math.round((d2-d1)/(86400000)));
        const span=days<7?`${days} days`:days<60?`${Math.round(days/7)} weeks`:`${Math.round(days/30)} months`;
        return days>0?<div style={{textAlign:"center",fontSize:11,color:V.accent,fontWeight:600,padding:"4px 0"}}>{span} apart</div>:null;
      })()}
      {/* Slider comparison */}
      <div ref={containerRef} onMouseMove={e=>{if(e.buttons===1)handleSlide(e);}} onTouchMove={handleSlide}
        style={{position:"relative",borderRadius:14,overflow:"hidden",aspectRatio:"3/4",border:`1px solid ${V.cardBorder}`,cursor:"col-resize",touchAction:"none"}}>
        {/* Right (after) image — full */}
        <img src={localSrc(photos[rightIdx])} alt="After photo" style={{position:"absolute",top:0,left:0,right:0,bottom:0,width:"100%",height:"100%",objectFit:"cover"}}/>
        {/* Left (before) image — clipped */}
        <div style={{position:"absolute",top:0,left:0,right:0,bottom:0,width:`${sliderPos}%`,overflow:"hidden"}}>
          <img src={localSrc(photos[leftIdx])} alt="Before photo" style={{position:"absolute",top:0,left:0,width:containerRef.current?containerRef.current.offsetWidth+"px":"100%",height:"100%",objectFit:"cover"}}/>
        </div>
        {/* Slider line */}
        <div style={{position:"absolute",top:0,bottom:0,left:`${sliderPos}%`,width:3,background:V.accent,transform:"translateX(-50%)",zIndex:10}}>
          <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",width:32,height:32,borderRadius:16,
            background:V.accent,display:"flex",alignItems:"center",justifyContent:"center",boxShadow:"0 2px 8px rgba(0,0,0,0.4)"}}>
            <span style={{color:V.bg,fontSize:12,fontWeight:900}}>⟷</span>
          </div>
        </div>
        {/* Labels */}
        <div style={{position:"absolute",bottom:8,left:8,padding:"4px 8px",borderRadius:6,background:"rgba(0,0,0,0.7)",fontSize:11,color:"#fff",fontWeight:600,zIndex:5}}>
          {fmtShort(photos[leftIdx]?.date)}
        </div>
        <div style={{position:"absolute",bottom:8,right:8,padding:"4px 8px",borderRadius:6,background:"rgba(0,0,0,0.7)",fontSize:11,color:"#fff",fontWeight:600,zIndex:5}}>
          {fmtShort(photos[rightIdx]?.date)}
        </div>
      </div>
    </div>
  );
}

// ─── AI Coach Chat (Claude API — BYOK + Free Tier) ───
export function useDataGuard(s){
  return useMemo(()=>{
    const warnings=[];
    // Body weight outliers
    const bw=s.body.filter(b=>parseFloat(b.weight)>0).sort((a,b)=>a.date.localeCompare(b.date));
    const kg=s.units==="kg";const BIG=kg?7:15,MED=kg?3.5:8; // thresholds in display units
    for(let i=1;i<bw.length;i++){
      const w1=parseFloat(bw[i].weight)||0,w0=parseFloat(bw[i-1].weight)||0;
      const diff=Math.abs(w1-w0);
      if(diff>BIG){warnings.push({type:"body",severity:"high",msg:`Weight jump: ${w0}→${w1} (${w1-w0>0?"+":""}${(w1-w0).toFixed(1)} ${s.units}) on ${bw[i].date}`,date:bw[i].date,id:bw[i].id});}
      else if(diff>MED){warnings.push({type:"body",severity:"med",msg:`Unusual weight change: ${diff.toFixed(1)} ${s.units} on ${bw[i].date}`,date:bw[i].date,id:bw[i].id});}
    }
    // Workout weight outliers per exercise
    const exMap={};
    s.workouts.forEach(w=>w.exercises.forEach(ex=>{
      ex.sets.forEach(st=>{if(st.weight>0){if(!exMap[ex.exerciseId])exMap[ex.exerciseId]=[];exMap[ex.exerciseId].push({w:st.weight,date:w.date,id:w.id});}});
    }));
    Object.entries(exMap).forEach(([eid,entries])=>{
      if(entries.length<3)return;
      const avg=entries.reduce((s2,e)=>s2+e.w,0)/entries.length;
      const std=Math.sqrt(entries.reduce((s2,e)=>s2+Math.pow(e.w-avg,2),0)/entries.length);
      entries.forEach(e=>{
        if(Math.abs(e.w-avg)>std*2.5&&std>10){
          const name=s.exercises.find(x=>x.id===eid)?.name||eid;
          warnings.push({type:"workout",severity:"med",msg:`${name}: ${e.w} ${s.units} is unusual (avg: ${Math.round(avg)}) on ${e.date}`,date:e.date,id:e.id});
        }
      });
    });
    // Duplicate workout detection (same date, very similar exercises)
    const dateMap={};
    s.workouts.forEach(w=>{if(!dateMap[w.date])dateMap[w.date]=[];dateMap[w.date].push(w);});
    Object.entries(dateMap).forEach(([date,ws])=>{
      if(ws.length>1){
        for(let i=1;i<ws.length;i++){
          const exA=ws[0].exercises.map(e=>e.exerciseId).sort().join(",");
          const exB=ws[i].exercises.map(e=>e.exerciseId).sort().join(",");
          if(exA===exB)warnings.push({type:"duplicate",severity:"high",msg:`Duplicate workout on ${date} (same exercises)`,date,id:ws[i].id});
          else warnings.push({type:"duplicate",severity:"low",msg:`Multiple workouts on ${date}`,date,id:ws[i].id});
        }
      }
    });
    // Calorie sanity
    s.nutrition.forEach(n=>{
      if(n.cal>6000)warnings.push({type:"nutrition",severity:"med",msg:`${n.cal} cal on ${n.date} seems high`,date:n.date,id:n.id});
      if(n.cal>0&&n.cal<500)warnings.push({type:"nutrition",severity:"low",msg:`Only ${n.cal} cal on ${n.date}`,date:n.date,id:n.id});
      if(n.protein>400)warnings.push({type:"nutrition",severity:"med",msg:`${n.protein}g protein on ${n.date} seems high`,date:n.date,id:n.id});
    });
    return warnings.sort((a,b)=>{const sev={high:0,med:1,low:2};return(sev[a.severity]||2)-(sev[b.severity]||2);});
  },[s.workouts,s.body,s.nutrition,s.exercises,s.units]);
}

export function DataGuardTab({s,d}){
  const warnings=useDataGuard(s);
  const sevColor={high:V.danger,med:V.warn,low:V.text3};
  const sevLabel={high:"Critical",med:"Warning",low:"Info"};

  const deleteItem=(w)=>{
    if(w.type==="workout"||w.type==="duplicate")d({type:"DEL_W",id:w.id});
    else if(w.type==="body")d({type:"DEL_B",id:w.id});
    else if(w.type==="nutrition")d({type:"DEL_N",id:w.id});
  };

  return(
    <div style={{display:"flex",flexDirection:"column",gap:12}}>
      <div style={{fontSize:16,fontWeight:800,color:V.text}}>Data Integrity</div>
      <div style={{fontSize:12,color:V.text3}}>Auto-scanned for outliers, duplicates, and suspicious values</div>

      {warnings.length===0?(
        <Card style={{padding:20,textAlign:"center"}}>
          <div style={{fontSize:28,marginBottom:8}}>✅</div>
          <div style={{fontSize:14,fontWeight:700,color:V.accent}}>All clear</div>
          <div style={{fontSize:11,color:V.text3,marginTop:4}}>No data issues detected across {s.workouts.length} workouts, {s.nutrition.length} nutrition logs, and {s.body.length} measurements</div>
        </Card>
      ):(
        <div>
          <div style={{fontSize:11,color:V.text3,marginBottom:8}}>{warnings.length} issue{warnings.length>1?"s":""} found</div>
          {warnings.map((w,i)=>(
            <Card key={i} style={{padding:12,marginBottom:6}}>
              <div style={{display:"flex",alignItems:"flex-start",gap:10}}>
                <div style={{width:8,height:8,borderRadius:4,background:sevColor[w.severity],marginTop:4,flexShrink:0}}/>
                <div style={{flex:1}}>
                  <div style={{fontSize:11,fontWeight:700,color:sevColor[w.severity],textTransform:"uppercase",marginBottom:2}}>{sevLabel[w.severity]}</div>
                  <div style={{fontSize:12,color:V.text,lineHeight:1.4}}>{w.msg}</div>
                </div>
                <button onClick={()=>ConfirmCtrl.show("Delete flagged entry?",w.msg,()=>deleteItem(w))} style={{padding:"5px 10px",borderRadius:6,
                  background:"rgba(255,107,107,0.08)",border:`1px solid rgba(255,107,107,0.15)`,cursor:"pointer",fontSize:11,color:V.danger,fontWeight:700,
                  flexShrink:0,whiteSpace:"nowrap"}}>Delete</button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── #2 Phase/Cycle Tracking ───
